import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from "react";
import { flushSync } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import TurndownService from "turndown";
import { gfm } from "turndown-plugin-gfm";
import DOMPurify from "dompurify";
import { useNavigate, useLocation } from "react-router-dom";
import countryData from "../../components/Languages.json";
import UploadFileIcon from "../../assets/images/icon/uploadfile.png";
import PasteIcon from "../../assets/images/icon/paste.png";
import AIIcon from "../../assets/images/icon/AI Icon.png";
import DropdownIcon from "../../assets/images/icon/dropdown.png";
import CopyIcon from "../../assets/images/icon/copy.png";
import ExportIcon from "../../assets/images/icon/export.png";
import WarningIcon from "../../assets/images/icon/warning.png";
import UndoIcon from "../../assets/images/icon/undo.png";
import FocusIcon from "../../assets/images/icon/focus.png";
import ViewIcon from "../../assets/images/icon/view.png";
import { BadgeCheckIcon, AlertCircle, Shield } from "lucide-react";
import useCustomScrollbar from "../../hooks/useCustomScrollbar";
import TextPanel from "../../components/common/TextPanel";
import { generatePdfFromHtml } from '../../utils/pdfFonts';
import { generateWordFromHtml } from '../../utils/wordExport';
import { markdownToHtml, MD_PDF_STYLE } from "../../utils/markdownHtml";
import {
  processTableContent,
  convertHtmlTablesToMarkdown,
  isTsvContent,
} from "../../utils/tableUtils";
import TranslateTabBar from "../../components/common/TranslateTabBar";
import TranscribeTab from "./TranscribeTab";
import { useLanguage } from "../../context/LanguageContext";
import TranslateService from "../../services/translate";
import PaymentService from "../../services/payment";
import { toastError, toastSuccess } from "../../components/Toast";
import MenuAIPanel from "../../components/common/MenuAIPanel";
import ContactUsPopup, { AlreadySentPopup } from "../../components/common/ContactUsPopup";
import { readStoredEmail, readStoredPlanName, readStoredGroupName } from "../../hooks/useSidebarData";
import AddIcon from "../../assets/images/icon/add.png";
import HistoryService from "../../services/history";
import { clearActiveTranslation } from "../../store/slices/userSlice";

const getTimestamp = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
};

const TRANSLATE_LS_KEY = "linko_translate_result";
const LAST_LANG_LS_KEY = "linko_last_selected_language";

const readLastLanguage = () => {
  try { return localStorage.getItem(LAST_LANG_LS_KEY) || ""; } catch (_e) { return ""; }
};

const saveLastLanguage = (name) => {
  if (!name) return;
  try { localStorage.setItem(LAST_LANG_LS_KEY, name); } catch (_e) { }
};

const saveTranslateToLS = (data) => {
  try { localStorage.setItem(TRANSLATE_LS_KEY, JSON.stringify(data)); } catch (_e) { }
};

// Custom markdown renderer with specific styling for the extracted text
const mdFromHtmlConverter = new TurndownService({
  headingStyle: "atx",
  bulletListMarker: "-",
  codeBlockStyle: "fenced",
  fence: "```",
  strongDelimiter: "**",
  emDelimiter: "*",
});
mdFromHtmlConverter.use(gfm);
mdFromHtmlConverter.escape = (string) => string;

mdFromHtmlConverter.addRule("compactBlocks", {
  filter: ["p", "div"],
  replacement: function (content) {
    return content.length ? `\n${content}` : "\n";
  },
});

mdFromHtmlConverter.addRule("keepMarks", {
  filter: "mark",
  replacement: function (content) {
    return `<mark>${content}</mark>`;
  },
});

const htmlToMarkdown = (html) => {
  if (!html || !html.replace(/<br\s*\/?>(?:\s|&nbsp;)*$/i, "").trim()) return "";
  const withMdTables = convertHtmlTablesToMarkdown(html);
  const clean = DOMPurify.sanitize(withMdTables, {
    ADD_TAGS: ["mark"],
    ADD_ATTR: ["class", "style"],
  });
  return mdFromHtmlConverter
    .turndown(clean)
    .replace(/^- {2,}/gm, "- ")
    .replace(/^(\d+\.) {2,}/gm, "$1 ")
    .trim();
};

// Custom ReactMarkdown components for better formatting
const MarkdownComponents = {
  // Style headings to match the image format with responsive scale
  h1: ({ children }) => <h1 style={{ fontFamily: 'Inter, sans-serif', fontSize: 'var(--translate-h1-size, 20px)', fontWeight: 'bold', color: '#3D2B90', marginTop: '10px', marginBottom: '6px' }}>{children}</h1>,
  h2: ({ children }) => <h2 style={{ fontFamily: 'Inter, sans-serif', fontSize: 'var(--translate-h2-size, 18px)', fontWeight: 'bold', color: '#3D2B90', marginTop: '8px', marginBottom: '5px' }}>{children}</h2>,
  h3: ({ children }) => <h3 style={{ fontFamily: 'Inter, sans-serif', fontSize: 'var(--translate-h3-size, 16px)', fontWeight: '600', color: '#3D2B90', marginTop: '6px', marginBottom: '4px' }}>{children}</h3>,
  h4: ({ children }) => <h4 style={{ fontFamily: 'Inter, sans-serif', fontSize: 'var(--translate-h4-size, 14px)', fontWeight: '600', color: '#3D2B90', marginTop: '5px', marginBottom: '3px' }}>{children}</h4>,
  h5: ({ children }) => <h5 style={{ fontFamily: 'Inter, sans-serif', fontSize: 'var(--translate-h5-size, 13px)', fontWeight: '600', color: '#3D2B90', marginTop: '4px', marginBottom: '3px' }}>{children}</h5>,
  h6: ({ children }) => <h6 style={{ fontFamily: 'Inter, sans-serif', fontSize: 'var(--translate-h6-size, 12px)', fontWeight: '600', color: '#3D2B90', marginTop: '4px', marginBottom: '3px' }}>{children}</h6>,
  // Style paragraphs to preserve whitespace and line breaks with responsive standard font size
  p: ({ children }) => <p style={{ fontFamily: 'Inter, sans-serif', fontSize: 'var(--translate-font-size, 15px)', fontWeight: '400', lineHeight: 'var(--translate-line-height, 1.52)', letterSpacing: 'normal', margin: '3px 0', whiteSpace: 'pre-wrap' }}>{children}</p>,
  // Style list items
  li: ({ children }) => <li style={{ fontFamily: 'Inter, sans-serif', fontSize: 'var(--translate-font-size, 15px)', fontWeight: '400', lineHeight: 'var(--translate-line-height, 1.52)', margin: '2px 0' }}>{children}</li>,
  // Style code blocks
  code: ({ inline, children }) => {
    if (inline) {
      return <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '4px', fontSize: '0.9em' }}>{children}</code>;
    }
    return <code style={{ display: 'block', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '8px', margin: '6px 0', whiteSpace: 'pre-wrap', fontSize: 'var(--translate-font-size, 14px)' }}>{children}</code>;
  },
  // Style blockquotes
  blockquote: ({ children }) => <blockquote style={{ borderLeft: '4px solid #3D2B90', paddingLeft: '14px', margin: '6px 0', color: '#555' }}>{children}</blockquote>,
  // Style strong/bold text
  strong: ({ children }) => <strong style={{ fontWeight: '700', color: '#2d1b6e' }}>{children}</strong>,
  // Style mark tags for highlighted personal identifiers
  mark: ({ node, children, ...props }) => (
    <mark
      className="bg-[#D8CEF6] text-[#3D2B90] font-semibold px-1.5 py-0.5 rounded-[4px] border-0 inline [&_mark]:p-0 [&_mark]:border-0 [&_mark]:bg-transparent"
      style={{ fontFamily: 'Inter, sans-serif' }}
      {...props}
    >
      {children}
    </mark>
  ),
  // Style tables with responsive overflow and clean brand borders
  table: ({ node, ...props }) => (
    <div
      style={{
        overflowX: 'auto',
        marginTop: '10px',
        marginBottom: '10px',
        maxWidth: '100%',
        borderRadius: '8px',
        border: '1px solid #E2DCF0',
      }}
    >
      <table
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          fontFamily: 'Inter, sans-serif',
          fontSize: 'var(--translate-table-font-size, 13px)',
          lineHeight: '1.42',
          color: '#2d1b6e',
        }}
        {...props}
      />
    </div>
  ),
  thead: ({ node, ...props }) => (
    <thead
      style={{
        background: '#F3F0FA',
        borderBottom: '2px solid #D6CCE8',
      }}
      {...props}
    />
  ),
  tbody: ({ node, ...props }) => (
    <tbody {...props} />
  ),
  tr: ({ node, ...props }) => (
    <tr
      style={{
        borderBottom: '1px solid #EAE6F4',
      }}
      {...props}
    />
  ),
  th: ({ node, ...props }) => (
    <th
      style={{
        padding: '7px 11px',
        textAlign: 'left',
        fontWeight: '700',
        color: '#3D2B90',
        borderRight: '1px solid #E2DCF0',
        whiteSpace: 'nowrap',
      }}
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td
      style={{
        padding: '7px 11px',
        textAlign: 'left',
        borderRight: '1px solid #EAE6F4',
        wordBreak: 'break-word',
      }}
      {...props}
    />
  ),
};

function FreezeWhileEditing({ focused, children }) {
  const frozenRef = useRef(children);
  if (!focused) frozenRef.current = children;
  return focused ? frozenRef.current : children;
}

const isMacPlatform = () => {
  if (typeof navigator === "undefined") return false;
  const platform = navigator.userAgentData?.platform || navigator.platform || "";
  const ua = navigator.userAgent || "";
  return /Mac|iPhone|iPod|iPad/i.test(platform) || /Macintosh/i.test(ua);
};

const Translate = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const reduxUser = useSelector((state) => state.user?.user);
  const reduxTokenBalance = useSelector((state) => state.user?.tokenBalance);
  const reduxTokenLimit = useSelector((state) => state.user?.tokenLimit);
  const reduxPersonalIdentifiersProtected = useSelector(
    (state) => state.user?.activeTranslation?.personalIdentifiersProtected
  );
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadConfirmActionRef = useRef("new");
  const originalRef = useRef(null);
  const translatedRef = useRef(null);
  const translatedNearBottomRef = useRef(true);
  const translatedUserScrolledRef = useRef(false);
  const focusOrigRef = useRef(null);
  const focusTransRef = useRef(null);
  const actionBarRef = useRef(null);

  const [originalText, setOriginalText] = useState(() => {
    try {
      const pendingDocStr = localStorage.getItem("linko_pending_doc_extract");
      if (pendingDocStr) {
        const pDoc = JSON.parse(pendingDocStr);
        if (pDoc?.extractedText && Date.now() - (pDoc.timestamp || 0) < 5 * 60 * 1000) {
          return pDoc.extractedText;
        }
      }
      const pendingStr = localStorage.getItem("linko_pending_translation");
      if (pendingStr) {
        const pending = JSON.parse(pendingStr);
        if (pending?.originalText && Date.now() - (pending.timestamp || 0) < 5 * 60 * 1000) {
          return pending.originalText;
        }
      }
      const s = localStorage.getItem(TRANSLATE_LS_KEY);
      if (s) return JSON.parse(s).originalText || "";
    } catch (_e) { }
    return "";
  });
  const [translatedText, setTranslatedText] = useState(() => {
    try {
      const activeStr = localStorage.getItem("linko_active_translation");
      if (activeStr) {
        const active = JSON.parse(activeStr);
        if (active?.translatedText && Date.now() - (active.timestamp || 0) < 5 * 60 * 1000) {
          return active.translatedText;
        }
      }
      const pendingStr = localStorage.getItem("linko_pending_translation");
      if (pendingStr) {
        const pending = JSON.parse(pendingStr);
        if (pending?.originalText && pending?.selectedLanguage && Date.now() - (pending.timestamp || 0) < 5 * 60 * 1000) {
          return "";
        }
      }
      const s = localStorage.getItem(TRANSLATE_LS_KEY);
      if (s) return JSON.parse(s).translatedText || "";
    } catch (_e) { }
    return "";
  });
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    try {
      const pendingStr = localStorage.getItem("linko_pending_translation");
      if (pendingStr) {
        const pending = JSON.parse(pendingStr);
        if (pending?.selectedLanguage && Date.now() - (pending.timestamp || 0) < 5 * 60 * 1000) {
          return pending.selectedLanguage;
        }
      }
      const last = readLastLanguage();
      if (last) return last;
      const s = localStorage.getItem(TRANSLATE_LS_KEY);
      if (s) return JSON.parse(s).selectedLanguage || "";
    } catch (_e) { }
    return "";
  });
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [langSearch, setLangSearch] = useState("");
  const [activeTab, setActiveTab] = useState(location.state?.tab || (location.pathname === "/transcribe" ? "transcribe" : "translate"));
  const [wordCount, setWordCount] = useState(() => {
    try { const s = localStorage.getItem(TRANSLATE_LS_KEY); if (s) return JSON.parse(s).wordCount || 0; } catch (_e) { }
    return 0;
  });
  const [viewMode, setViewMode] = useState("horizontal");
  const [copiedOriginal, setCopiedOriginal] = useState(false);
  const [copiedTranslated, setCopiedTranslated] = useState(false);
  const [isTranslating, setIsTranslating] = useState(() => {
    try {
      const pendingStr = localStorage.getItem("linko_pending_translation");
      if (pendingStr) {
        const pending = JSON.parse(pendingStr);
        if (pending?.originalText && pending?.selectedLanguage && Date.now() - (pending.timestamp || 0) < 5 * 60 * 1000) {
          return true;
        }
      }
    } catch (_e) { }
    return false;
  });
  const [isUploadingDoc, setIsUploadingDoc] = useState(() => {
    return TranslateService.isDocExtracting();
  });
  const [isStreaming, setIsStreaming] = useState(() => {
    try {
      const pendingStr = localStorage.getItem("linko_pending_translation");
      if (pendingStr) {
        const pending = JSON.parse(pendingStr);
        if (pending?.originalText && pending?.selectedLanguage && Date.now() - (pending.timestamp || 0) < 5 * 60 * 1000) {
          return true;
        }
      }
    } catch (_e) { }
    return false;
  });
  const [streamingText, setStreamingText] = useState("");
  const streamingParagraphsRef = useRef({});
  const [showExportPicker, setShowExportPicker] = useState(false);
  const [langError, setLangError] = useState(false);
  const [showLangMsg, setShowLangMsg] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [showAiTooltip, setShowAiTooltip] = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);
  const [isContactUsSubmitting, setIsContactUsSubmitting] = useState(false);
  const [contactUsSuccess, setContactUsSuccess] = useState(false);
  const [showAlreadySentPopup, setShowAlreadySentPopup] = useState(false);

  const isContactRequestSent = useCallback(() => {
    const note = localStorage.getItem("linko_rejection_note");
    if (note) return false;
    return (
      localStorage.getItem("linko_contact_sent") === "true" ||
      reduxUser?.is_contactus === true ||
      reduxUser?.is_contact_request === true ||
      reduxUser?.contact_request === true ||
      reduxUser?.is_request === true
    );
  }, [reduxUser]);
  const [glossaryOn, setGlossaryOn] = useState(false);
  const [focusPickerOpen, setFocusPickerOpen] = useState(false);
  const [focusPanel, setFocusPanel] = useState(null);
  const [showNewTranslateConfirm, setShowNewTranslateConfirm] = useState(false);
  const [isClearingTranslate, setIsClearingTranslate] = useState(false);
  const [showNewTranscribeConfirm, setShowNewTranscribeConfirm] = useState(false);

  const handleContactUsSubmit = async (form) => {
    try {
      setIsContactUsSubmitting(true);
      const payload = {
        email: form.email,
        name: form.name,
        phone_no: form.phone_no,
        country_code: form.country_code,
        note: form.note,
      };
      await PaymentService.contactUsPlan(payload);
      setShowContactUs(false);
      setContactUsSuccess(true);
      try {
        localStorage.setItem("linko_contact_sent", "true");
        localStorage.removeItem("linko_rejection_note");
      } catch (_) {}
      window.dispatchEvent(new CustomEvent("contactStatusChanged", { detail: { sent: true } }));
      window.dispatchEvent(new Event("planUpdated"));
      window.dispatchEvent(new Event("profileUpdated"));
    } catch (error) {
      toastError(
        error?.response?.data?.message ||
        "Failed to send request. Please try again."
      );
    } finally {
      setIsContactUsSubmitting(false);
    }
  };
  const [transcribeHasContent, setTranscribeHasContent] = useState(false);
  const [transcribeCanUndo, setTranscribeCanUndo] = useState(false);
  const [transcribeCanRedo, setTranscribeCanRedo] = useState(false);
  const [translateCanUndo, setTranslateCanUndo] = useState(false);
  const [translateCanRedo, setTranslateCanRedo] = useState(false);
  const [translateError, setTranslateError] = useState("");
  const [personalIdentifiersProtected, setPersonalIdentifiersProtected] = useState(() => {
    try {
      const s = localStorage.getItem(TRANSLATE_LS_KEY);
      if (s) {
        const parsed = JSON.parse(s);
        if (typeof parsed.personalIdentifiersProtected === "number") {
          return parsed.personalIdentifiersProtected;
        }
      }
    } catch (_e) { }
    return null;
  });
  const personalIdentifiersProtectedRef = useRef(personalIdentifiersProtected);
  useEffect(() => {
    personalIdentifiersProtectedRef.current = personalIdentifiersProtected;
  }, [personalIdentifiersProtected]);

  const applyPersonalIdentifiersProtected = (value) => {
    if (value === null || value === undefined || value === "" || isNaN(Number(value))) return;
    const num = Number(value);
    personalIdentifiersProtectedRef.current = num;
    setPersonalIdentifiersProtected((prev) => (prev === num ? prev : num));
  };
  const [privacyStatus, setPrivacyStatus] = useState("default");
  const privacyTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (privacyTimerRef.current) clearTimeout(privacyTimerRef.current);
    };
  }, []);

  const historyStackRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const originalTextRef = useRef("");
  const translatedTextRef = useRef("");
  const origDraftRef = useRef(null);
  const transDraftRef = useRef(null);
  const origHtmlDraftRef = useRef(null);
  const transHtmlDraftRef = useRef(null);
  const liveSaveTimerRef = useRef(null);
  const origLastSelectionRef = useRef(null);
  const transLastSelectionRef = useRef(null);
  const isProcessingRef = useRef(false);

  const [textHistory, setTextHistory] = useState([]);
  const [docHistory, setDocHistory] = useState([]);
  const [audioHistory, setAudioHistory] = useState([]);
  const [latestAudioTranslation, setLatestAudioTranslation] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const isFetchingHistoryRef = useRef(false);
  const lastFetchedTabRef = useRef(null);
  const [presets, setPresets] = useState([]);

  // Socket-level capture (survives per-translation listener teardown)
  useEffect(() => {
    const handler = (e) => {
      applyPersonalIdentifiersProtected(e?.detail?.value);
    };
    window.addEventListener("personalIdentifiersUpdated", handler);
    try {
      const cached = JSON.parse(localStorage.getItem("linko_personal_identifiers_protected") || "null");
      if (cached && cached.value !== null && cached.value !== undefined) {
        applyPersonalIdentifiersProtected(cached.value);
      }
    } catch (_e) { }
    return () => window.removeEventListener("personalIdentifiersUpdated", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redux mirror written by the translate service
  useEffect(() => {
    applyPersonalIdentifiersProtected(reduxPersonalIdentifiersProtected);
  }, [reduxPersonalIdentifiersProtected]);

  const [availableTokens, setAvailableTokens] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("linko_token_status") || "null");
      return typeof cached?.availableTokens === "number" ? cached.availableTokens : null;
    } catch (_) { return null; }
  });
  const [tokenTotal, setTokenTotal] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem("linko_token_status") || "null");
      return typeof cached?.tokenTotal === "number" ? cached.tokenTotal : null;
    } catch (_) { return null; }
  });

  useEffect(() => {
    if (reduxTokenBalance !== undefined && reduxTokenBalance !== null) {
      setAvailableTokens(reduxTokenBalance);
    }
  }, [reduxTokenBalance]);

  useEffect(() => {
    if (reduxTokenLimit !== undefined && reduxTokenLimit !== null) {
      setTokenTotal(reduxTokenLimit);
    }
  }, [reduxTokenLimit]);

  const checkAiAccess = useCallback(() => {
    let cachedUser = null;
    try {
      cachedUser = JSON.parse(localStorage.getItem("linko_user_profile") || "null");
    } catch (_) {}
    const isMissingVal = (v) => !v || v === "undefined" || v === "null";
    const rawPlan = readStoredPlanName();
    const validPlan = !isMissingVal(rawPlan) ? rawPlan : "";

    const storedPlan = (
      validPlan ||
      reduxUser?.plan_name ||
      cachedUser?.plan_name ||
      reduxUser?.trainingGroupId?.plan_name ||
      cachedUser?.trainingGroupId?.plan_name ||
      ""
    ).toLowerCase();

    const rawGroup = readStoredGroupName();
    const validGroup = !isMissingVal(rawGroup) ? rawGroup : "";

    const storedGroup = (
      validGroup ||
      reduxUser?.trainingGroupId?.groupName ||
      cachedUser?.trainingGroupId?.groupName ||
      ""
    ).toLowerCase();

    return (
      storedPlan.includes("adminprovide") ||
      (!isMissingVal(storedPlan) && (storedPlan.includes("org") || storedPlan.includes("organiza") || storedPlan.includes("contact"))) ||
      (!isMissingVal(storedGroup) && Boolean(storedGroup)) ||
      Boolean(reduxUser?.trainingGroupId) ||
      Boolean(cachedUser?.trainingGroupId)
    );
  }, [reduxUser]);

  const [hasAiAccess, setHasAiAccess] = useState(() => {
    try {
      let cachedUser = null;
      try {
        cachedUser = JSON.parse(localStorage.getItem("linko_user_profile") || "null");
      } catch (_) {}
      const isMissingVal = (v) => !v || v === "undefined" || v === "null";
      const rawPlan = readStoredPlanName();
      const validPlan = !isMissingVal(rawPlan) ? rawPlan : "";
      const storedPlan = (
        validPlan ||
        cachedUser?.plan_name ||
        cachedUser?.trainingGroupId?.plan_name ||
        ""
      ).toLowerCase();
      const rawGroup = readStoredGroupName();
      const validGroup = !isMissingVal(rawGroup) ? rawGroup : "";
      const storedGroup = (
        validGroup ||
        cachedUser?.trainingGroupId?.groupName ||
        ""
      ).toLowerCase();
      return (
        storedPlan.includes("adminprovide") ||
        (!isMissingVal(storedPlan) && (storedPlan.includes("org") || storedPlan.includes("organiza") || storedPlan.includes("contact"))) ||
        (!isMissingVal(storedGroup) && Boolean(storedGroup)) ||
        Boolean(cachedUser?.trainingGroupId)
      );
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    setHasAiAccess(checkAiAccess());
  }, [checkAiAccess]);

  useEffect(() => {
    const handleUpdate = () => {
      setHasAiAccess(checkAiAccess());
    };
    window.addEventListener("profileUpdated", handleUpdate);
    window.addEventListener("planUpdated", handleUpdate);
    window.addEventListener("authChanged", handleUpdate);
    return () => {
      window.removeEventListener("profileUpdated", handleUpdate);
      window.removeEventListener("planUpdated", handleUpdate);
      window.removeEventListener("authChanged", handleUpdate);
    };
  }, [checkAiAccess]);
  const [showUploadTooltip, setShowUploadTooltip] = useState(false);

  const isTokenExhausted =
    availableTokens === 0 ||
    (tokenTotal > 0 && availableTokens !== null && availableTokens <= 0);

  const isLimitReached =
    (tokenTotal > 0 &&
      availableTokens !== null &&
      ((tokenTotal - availableTokens) / tokenTotal) * 100 >= 90) ||
    availableTokens === 0;

  useEffect(() => {
    try {
      const s = localStorage.getItem(TRANSLATE_LS_KEY);
      if (s) {
        const d = JSON.parse(s);
        if (typeof d.personalIdentifiersProtected === "number") {
          setPersonalIdentifiersProtected(d.personalIdentifiersProtected);
        }
        if (d.selectedLanguage) selectedLanguageRef.current = d.selectedLanguage;
        if (d.originalText) savedOriginalRef.current = d.originalText;
        if (d.translatedText) savedTranslatedRef.current = d.translatedText;
        if (d.translationId) currentTranslationIdRef.current = d.translationId;
        if (d.translationType) { currentTranslationTypeRef.current = d.translationType; setCurrentTranslationType(d.translationType); }
        if (Array.isArray(d.historyStack) && d.historyStack.length > 0) {
          const hasAnyContent = d.historyStack.some((item) => (item.originalText || "").trim() || (item.translatedText || "").trim());
          if (!hasAnyContent) {
            historyStackRef.current = [{
              originalText: d.originalText || "",
              translatedText: d.translatedText || "",
              selectedLanguage: d.selectedLanguage || "",
              translationId: d.translationId || null,
              translationType: d.translationType || null,
              wordCount: d.wordCount || 0,
              saved: true,
            }];
            historyIndexRef.current = 0;
          } else {
            historyStackRef.current = d.historyStack;
            const validIndex = Math.max(0, Math.min(typeof d.historyIndex === "number" ? d.historyIndex : d.historyStack.length - 1, d.historyStack.length - 1));
            historyIndexRef.current = validIndex;
          }
        } else {
          historyStackRef.current = [{
            originalText: d.originalText || "",
            translatedText: d.translatedText || "",
            selectedLanguage: d.selectedLanguage || "",
            translationId: d.translationId || null,
            translationType: d.translationType || null,
            wordCount: d.wordCount || 0,
            saved: true,
          }];
          historyIndexRef.current = 0;
        }
        syncTranslateUndoRedo();
      } else {
        historyStackRef.current = [{
          originalText: "",
          translatedText: "",
          selectedLanguage: "",
          translationId: null,
          translationType: null,
          wordCount: 0,
          saved: true,
        }];
        historyIndexRef.current = 0;
        setTranslateCanUndo(false);
        setTranslateCanRedo(false);
      }
    } catch (_e) {
      historyStackRef.current = [{
        originalText: "",
        translatedText: "",
        selectedLanguage: "",
        translationId: null,
        translationType: null,
        wordCount: 0,
        saved: true,
      }];
      historyIndexRef.current = 0;
      setTranslateCanUndo(false);
      setTranslateCanRedo(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrolledToBottomRef = useRef(false);
  const origScrollPosRef = useRef({ h: 0, top: 0, client: 0 });
  const transScrollPosRef = useRef({ h: 0, top: 0, client: 0 });

  useLayoutEffect(() => {
    if (!originalText && !translatedText) return;
    const AUTO_SCROLL_THRESHOLD = 80;
    const orig = originalRef.current;
    const trans = translatedRef.current;
    const origPrev = origScrollPosRef.current;
    const transPrev = transScrollPosRef.current;
    const origWasNearBottom = origPrev.h > 0 && origPrev.h - origPrev.top - origPrev.client <= AUTO_SCROLL_THRESHOLD;
    const transWasNearBottom = transPrev.h > 0 && transPrev.h - transPrev.top - transPrev.client <= AUTO_SCROLL_THRESHOLD;

    requestAnimationFrame(() => {
      if (orig && origWasNearBottom && isStreaming) {
        orig.scrollTop = orig.scrollHeight;
      }
      if (trans && transWasNearBottom && isStreaming) {
        trans.scrollTop = trans.scrollHeight;
      }
      scrolledToBottomRef.current = true;
      if (originalRef.current) origScrollPosRef.current = { h: originalRef.current.scrollHeight, top: originalRef.current.scrollTop, client: originalRef.current.clientHeight };
      if (translatedRef.current) transScrollPosRef.current = { h: translatedRef.current.scrollHeight, top: translatedRef.current.scrollTop, client: translatedRef.current.clientHeight };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalText, translatedText, isStreaming]);

  const [editEpoch, setEditEpoch] = useState(0);
  const [origFocused, setOrigFocused] = useState(false);
  const [transFocused, setTransFocused] = useState(false);
  const [focusOrigFocused, setFocusOrigFocused] = useState(false);
  const [focusTransFocused, setFocusTransFocused] = useState(false);
  const [origDraftActive, setOrigDraftActive] = useState(false);
  const [transDraftActive, setTransDraftActive] = useState(false);
  const origCommitScrollRef = useRef(0);
  const transCommitScrollRef = useRef(0);

  useEffect(() => {
    if (activeTab !== "translate") return;
    const capture = () => {
      if (originalRef.current) origScrollPosRef.current = { h: originalRef.current.scrollHeight, top: originalRef.current.scrollTop, client: originalRef.current.clientHeight };
      if (translatedRef.current) transScrollPosRef.current = { h: translatedRef.current.scrollHeight, top: translatedRef.current.scrollTop, client: translatedRef.current.clientHeight };
    };
    capture();
    const els = [originalRef.current, translatedRef.current].filter(Boolean);
    els.forEach((el) => el.addEventListener("scroll", capture, { passive: true }));
    return () => els.forEach((el) => el.removeEventListener("scroll", capture));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, editEpoch]);

  useEffect(() => {
    if (isTranslating || isStreaming) return;
    saveTranslateToLS({
      originalText,
      translatedText,
      selectedLanguage,
      wordCount,
      translationId: currentTranslationIdRef.current,
      translationType: currentTranslationTypeRef.current,
      historyStack: historyStackRef.current,
      historyIndex: historyIndexRef.current,
      personalIdentifiersProtected,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalText, translatedText, selectedLanguage, wordCount, personalIdentifiersProtected, isTranslating, isStreaming]);

  useEffect(() => {
    if (activeTab !== "translate") return;
    scrolledToBottomRef.current = false;
    const scrollToTop = () => {
      if (originalRef.current) originalRef.current.scrollTop = 0;
      if (translatedRef.current) translatedRef.current.scrollTop = 0;
      requestAnimationFrame(() => {
        originalScroll.update();
        translatedScroll.update();
      });
    };
    scrollToTop();
    const st1 = setTimeout(scrollToTop, 100);
    const st2 = setTimeout(scrollToTop, 300);

    // Sync state if coming from tab switch or doc extract
    try {
      const pendingDocStr = localStorage.getItem("linko_pending_doc_extract");
      if (pendingDocStr) {
        const pDoc = JSON.parse(pendingDocStr);
        if (pDoc?.extractedText) {
          setOriginalText(pDoc.extractedText);
          originalTextRef.current = pDoc.extractedText;
          savedOriginalRef.current = pDoc.extractedText;
          lastSavedOriginalRef.current = pDoc.extractedText;
          setWordCount(pDoc.extractedText.trim() === "" ? 0 : pDoc.extractedText.trim().split(/\s+/).length);
          setEditEpoch((e) => e + 1);
          setIsUploadingDoc(false);
        } else if (TranslateService.isDocExtracting()) {
          setIsUploadingDoc(true);
        }
      }
      const s = localStorage.getItem(TRANSLATE_LS_KEY);
      if (s) {
        const parsed = JSON.parse(s);
        if (parsed.originalText && (!originalTextRef.current || originalTextRef.current !== parsed.originalText)) {
          setOriginalText(parsed.originalText);
          originalTextRef.current = parsed.originalText;
          savedOriginalRef.current = parsed.originalText;
          lastSavedOriginalRef.current = parsed.originalText;
          setWordCount(parsed.wordCount || parsed.originalText.trim().split(/\s+/).length);
          setEditEpoch((e) => e + 1);
        }
      }
    } catch (_e) { }

    const fetchHistory = async () => {
      if (isFetchingHistoryRef.current) return;
      isFetchingHistoryRef.current = true;
      setHistoryLoading(true);
      try {
        const textRes = await HistoryService.getTextTranslations().catch(() => null);
        const texts = textRes?.data?.data || textRes?.data || [];
        setTextHistory(Array.isArray(texts) ? texts : []);
      } finally {
        setHistoryLoading(false);
        isFetchingHistoryRef.current = false;
      }
    };
    if (lastFetchedTabRef.current !== activeTab) {
      lastFetchedTabRef.current = activeTab;
      fetchHistory();
    }
    return () => { clearTimeout(st1); clearTimeout(st2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    const resumePending = async () => {
      try {
        const pendingStr = localStorage.getItem("linko_pending_translation");
        if (!pendingStr) return;
        const pending = JSON.parse(pendingStr);
        if (!pending?.originalText || !pending?.selectedLanguage) {
          localStorage.removeItem("linko_pending_translation");
          return;
        }
        if (Date.now() - (pending.timestamp || 0) > 5 * 60 * 1000) {
          localStorage.removeItem("linko_pending_translation");
          return;
        }

        const activeStr = localStorage.getItem("linko_active_translation");
        let initialPartial = "";
        let isCompletedActive = false;
        if (activeStr) {
          try {
            const active = JSON.parse(activeStr);
            if (active?.translatedText && Date.now() - (active.timestamp || 0) < 5 * 60 * 1000) {
              initialPartial = active.translatedText;
              if (active.status === "completed") {
                isCompletedActive = true;
              }
            }
          } catch (_e) { }
        }

        // Set the original text & language
        setOriginalText(pending.originalText);
        setSelectedLanguage(pending.selectedLanguage);
        selectedLanguageRef.current = pending.selectedLanguage;

        // If completed or if active translation completed, restore without re-emitting
        if (isCompletedActive) {
          const finalTr = initialPartial;
          if (finalTr) {
            setTranslatedText(finalTr);
            savedTranslatedRef.current = finalTr;
            lastSavedTranslatedRef.current = finalTr;
            translatedTextRef.current = finalTr;
          }
          savedOriginalRef.current = pending.originalText;
          lastSavedOriginalRef.current = pending.originalText;
          setIsTranslating(false);
          setIsStreaming(false);
          setStreamingText("");
          localStorage.removeItem("linko_pending_translation");
          return;
        }

        // If translation is pending/streaming, keep loader active and resume translation via socket
        setIsTranslating(true);
        setIsStreaming(true);
        setStreamingText(initialPartial || "");
        streamingParagraphsRef.current = {};
        setPrivacyStatus("protecting");

        let accumulated = initialPartial || "";
        let streamPi = null;
        try {
          const streamRes = await TranslateService.translateTextViaSocket(
            pending.originalText,
            pending.selectedLanguage,
            (event) => {
              if (typeof event?.personalIdentifiersProtected === "number") {
                streamPi = event.personalIdentifiersProtected;
                setPersonalIdentifiersProtected(event.personalIdentifiersProtected);
              }
              if (event.type === "paragraph") {
                streamingParagraphsRef.current[event.index] = event.translation;
                const fullText = Object.keys(streamingParagraphsRef.current)
                  .sort((a, b) => Number(a) - Number(b))
                  .map((k) => streamingParagraphsRef.current[k])
                  .join("\n\n");
                accumulated = fullText;
                flushSync(() => {
                  setStreamingText(fullText);
                  setTranslatedText(fullText);
                });
              } else if (event.type === "chunk") {
                accumulated = event.accumulated;
                flushSync(() => {
                  setStreamingText(accumulated);
                  setTranslatedText(accumulated);
                });
              } else if (event.type === "done") {
                accumulated = event.translation || accumulated;
                if (typeof event?.personalIdentifiersProtected === "number") {
                  streamPi = event.personalIdentifiersProtected;
                  setPersonalIdentifiersProtected(event.personalIdentifiersProtected);
                }
              }
            },
            dispatch
          );

          if (typeof streamRes?.personalIdentifiersProtected === "number") {
            setPersonalIdentifiersProtected(streamRes.personalIdentifiersProtected);
          } else if (typeof streamPi === "number") {
            setPersonalIdentifiersProtected(streamPi);
          }

          const finalTranslation = accumulated;
          currentTranslationIdRef.current = null;
          currentTranslationTypeRef.current = "text";
          setCurrentTranslationType("text");
          translatedTextRef.current = finalTranslation;
          originalTextRef.current = pending.originalText;
          setTranslatedText(finalTranslation);
          savedTranslatedRef.current = finalTranslation;
          lastSavedTranslatedRef.current = finalTranslation;
          setIsTranslatedDirty(false);
          savedOriginalRef.current = pending.originalText;
          lastSavedOriginalRef.current = pending.originalText;
          setIsTextDirty(false);
          setIsStreaming(false);
          setStreamingText("");
          streamingParagraphsRef.current = {};
          commitTranslateToHistory(true);
          refreshTextHistory();
          setPrivacyStatus("completed");
          if (privacyTimerRef.current) clearTimeout(privacyTimerRef.current);
          privacyTimerRef.current = setTimeout(() => {
            setPrivacyStatus("reminder");
          }, 5000);
        } catch (error) {
          const partialTranslation = accumulated;
          if (partialTranslation) {
            setTranslatedText(partialTranslation);
            savedTranslatedRef.current = partialTranslation;
            lastSavedTranslatedRef.current = partialTranslation;
            translatedTextRef.current = partialTranslation;
          }
          const errMsg =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.data?.message ||
            error?.data?.error ||
            error?.message ||
            t.translationFailed;
          setTranslateError(errMsg);
          toastError(errMsg);
        } finally {
          setIsTranslating(false);
          setIsStreaming(false);
          localStorage.removeItem("linko_pending_translation");
        }
      } catch (_e) {
      }
    };

    const resumePendingDoc = async () => {
      try {
        const pendingDocStr = localStorage.getItem("linko_pending_doc_extract");
        if (!pendingDocStr) return;
        const pendingDoc = JSON.parse(pendingDocStr);
        if (Date.now() - (pendingDoc?.timestamp || 0) > 5 * 60 * 1000) {
          localStorage.removeItem("linko_pending_doc_extract");
          setIsUploadingDoc(false);
          return;
        }

        if (pendingDoc?.extractedText) {
          const text = pendingDoc.extractedText;
          setOriginalText(text);
          originalTextRef.current = text;
          savedOriginalRef.current = text;
          lastSavedOriginalRef.current = text;
          const words = text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
          setWordCount(words);
          currentTranslationIdRef.current = null;
          currentTranslationTypeRef.current = "document";
          setCurrentTranslationType("document");
          setEditEpoch((e) => e + 1);
          setIsUploadingDoc(false);
          try {
            localStorage.removeItem("linko_pending_doc_extract");
          } catch (_e) { }
          return;
        }

        if (TranslateService.isDocExtracting()) {
          setIsUploadingDoc(true);
        } else {
          try {
            localStorage.removeItem("linko_pending_doc_extract");
          } catch (_e) { }
          setIsUploadingDoc(false);
        }
      } catch (_e) {
      }
    };

    resumePending();
    resumePendingDoc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleExtractCompleted = (e) => {
      const text = e.detail?.text || "";
      const words = text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
      currentTranslationIdRef.current = null;
      currentTranslationTypeRef.current = "document";
      setCurrentTranslationType("document");
      originalTextRef.current = text;
      translatedTextRef.current = "";
      origDraftRef.current = null;
      origHtmlDraftRef.current = null;
      transDraftRef.current = null;
      transHtmlDraftRef.current = null;
      setOrigDraftActive(false);
      setOriginalText(text);
      setWordCount(words);
      setTranslatedText("");
      setTransDraftActive(false);
      savedTranslatedRef.current = "";
      lastSavedTranslatedRef.current = "";
      setIsTranslatedDirty(false);
      savedOriginalRef.current = text;
      lastSavedOriginalRef.current = text;
      setIsTextDirty(false);
      setUploadedFile(null);
      setEditEpoch((epoch) => epoch + 1);
      setIsUploadingDoc(false);
      isProcessingRef.current = false;
      resetTranslateHistoryToCurrent();
      requestAnimationFrame(() => {
        if (originalRef.current) originalRef.current.scrollTop = 0;
        if (translatedRef.current) translatedRef.current.scrollTop = 0;
      });
      try {
        localStorage.removeItem("linko_pending_doc_extract");
      } catch (_e) { }
    };

    const handleExtractFailed = (e) => {
      setIsUploadingDoc(false);
      isProcessingRef.current = false;
      setUploadedFile(null);
      try {
        localStorage.removeItem("linko_pending_doc_extract");
      } catch (_e) { }
    };

    const handleTranslationError = (e) => {
      const data = e.detail;
      const errMsg =
        data?.error ||
        data?.message ||
        data?.data?.message ||
        (typeof data === "string" ? data : "Translation failed");

      setTranslateError(errMsg);
      toastError(errMsg);
      setIsTranslating(false);
      setIsStreaming(false);
      setStreamingText("");
      streamingParagraphsRef.current = {};
      isProcessingRef.current = false;
      setPrivacyStatus("default");

      try {
        localStorage.removeItem("linko_pending_translation");
        localStorage.removeItem("linko_active_translation");
        dispatch(clearActiveTranslation());
      } catch (_e) { }
    };

    window.addEventListener("linko_doc_extract_completed", handleExtractCompleted);
    window.addEventListener("linko_doc_extract_failed", handleExtractFailed);
    window.addEventListener("translationError", handleTranslationError);

    return () => {
      window.removeEventListener("linko_doc_extract_completed", handleExtractCompleted);
      window.removeEventListener("linko_doc_extract_failed", handleExtractFailed);
      window.removeEventListener("translationError", handleTranslationError);
    };
  }, [dispatch]);

  useEffect(() => {
    const tab = location.state?.tab || (location.pathname === "/transcribe" ? "transcribe" : "translate");
    setActiveTab(tab);
  }, [location.pathname, location.state]);

  const handleSelectHistoryItem = (item) => {
    const orig = item.originalText || item.extractedText || item.transcription || "";
    const trans = item.translatedText || item.translation || "";
    const id = item._id || null;
    const type = item.extractedText ? "document" : "text";
    setOriginalText(orig);
    setTranslatedText(trans);
    savedTranslatedRef.current = trans;
    setIsTranslatedDirty(false);
    if (item.targetLanguage) { setSelectedLanguage(item.targetLanguage); selectedLanguageRef.current = item.targetLanguage; saveLastLanguage(item.targetLanguage); }
    const words = orig.trim() === "" ? 0 : orig.trim().split(/\s+/).length;
    setWordCount(words);
    setPersonalIdentifiersProtected(typeof item.personalIdentifiersProtected === "number" ? item.personalIdentifiersProtected : null);
    currentTranslationIdRef.current = id;
    currentTranslationTypeRef.current = id ? type : null;
    setCurrentTranslationType(id ? type : null);
    savedOriginalRef.current = orig;
    lastSavedOriginalRef.current = orig;
    lastSavedTranslatedRef.current = trans;
    originalTextRef.current = orig;
    translatedTextRef.current = trans;
    setIsTextDirty(false);
    resetTranslateHistoryToCurrent();
    if (activeTab !== "translate") setActiveTab("translate");
    requestAnimationFrame(() => {
      if (originalRef.current) originalRef.current.scrollTop = 0;
      if (translatedRef.current) translatedRef.current.scrollTop = 0;
      originalScroll.update();
      translatedScroll.update();
    });
  };

  const handleSendToTranslate = (text) => {
    if (!text) return;
    try {
      HistoryService.deleteTranslations().catch(() => {});
    } catch (_) { }
    try { localStorage.removeItem(TRANSLATE_LS_KEY); } catch (_e) { }
    try { localStorage.removeItem("linko_personal_identifiers_protected"); } catch (_e) { }
    setTextHistory([]);
    setDocHistory([]);

    setOriginalText(text);
    originalTextRef.current = text;
    setOrigDraftActive(false);
    origDraftRef.current = null;
    origHtmlDraftRef.current = null;

    setTranslatedText("");
    translatedTextRef.current = "";
    savedTranslatedRef.current = "";
    setTransDraftActive(false);
    transDraftRef.current = null;
    transHtmlDraftRef.current = null;
    setStreamingText("");
    setUploadedFile(null);

    const words = text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
    setWordCount(words);
    setEditEpoch((e) => e + 1);
    setPersonalIdentifiersProtected(null);
    currentTranslationIdRef.current = null;
    currentTranslationTypeRef.current = "text";
    setCurrentTranslationType("text");
    savedOriginalRef.current = "";
    setIsTextDirty(true);
    setIsTranslatedDirty(false);
    setShowLangMsg(!selectedLanguageRef.current);
    resetTranslateHistoryToCurrent();

    saveTranslateToLS({
      originalText: text,
      translatedText: "",
      wordCount: words,
      selectedLanguage: selectedLanguageRef.current || "",
    });

    setActiveTab("translate");
    navigate("/translate", { replace: true });

    requestAnimationFrame(() => {
      if (originalRef.current) originalRef.current.scrollTop = 0;
      if (translatedRef.current) translatedRef.current.scrollTop = 0;
      originalScroll.update();
      translatedScroll.update();
    });
  };

  const refreshAudioHistory = useCallback(() => { }, []);

  const refreshTextHistory = async () => {
    if (isFetchingHistoryRef.current) return;
    isFetchingHistoryRef.current = true;
    try {
      const textRes = await HistoryService.getTextTranslations().catch(() => null);
      const texts = textRes?.data?.data || textRes?.data || [];
      setTextHistory(Array.isArray(texts) ? texts : []);
    } finally {
      isFetchingHistoryRef.current = false;
    }
  };

  const transcribeResetRef = useRef(null);
  const transcribeUndoRef = useRef(null);
  const transcribeRedoRef = useRef(null);
  const lastSavedOriginalRef = useRef("");
  const lastSavedTranslatedRef = useRef("");
  const translateHistoryScrollRef = useRef(null);
  const currentTranslationIdRef = useRef(null);
  const currentTranslationTypeRef = useRef(null);
  const [currentTranslationType, setCurrentTranslationType] = useState(null);
  const retranslateTimerRef = useRef(null);
  const selectedLanguageRef = useRef("");
  const savedOriginalRef = useRef("");
  const [isTextDirty, setIsTextDirty] = useState(false);
  const [isTranslatedDirty, setIsTranslatedDirty] = useState(false);
  const savedTranslatedRef = useRef("");
  originalTextRef.current = originalText;
  translatedTextRef.current = translatedText;

  const captureTranslateState = (skipDraftConversion = false) => {
    let orig = originalTextRef.current || "";
    if (origHtmlDraftRef.current !== null) {
      orig = skipDraftConversion ? (origDraftRef.current !== null && origDraftRef.current !== "" ? origDraftRef.current : orig) : (htmlToMarkdown(origHtmlDraftRef.current) || orig);
    } else if (origDraftRef.current !== null && origDraftRef.current !== "") {
      orig = origDraftRef.current;
    }

    let trans = translatedTextRef.current || "";
    if (transHtmlDraftRef.current !== null) {
      trans = skipDraftConversion ? (transDraftRef.current !== null && transDraftRef.current !== "" ? transDraftRef.current : trans) : (htmlToMarkdown(transHtmlDraftRef.current) || trans);
    } else if (transDraftRef.current !== null && transDraftRef.current !== "") {
      trans = transDraftRef.current;
    }

    return {
      originalText: orig,
      translatedText: trans,
      selectedLanguage: selectedLanguageRef.current || "",
      translationId: currentTranslationIdRef.current || null,
      translationType: currentTranslationTypeRef.current || null,
      personalIdentifiersProtected: personalIdentifiersProtectedRef.current ?? personalIdentifiersProtected ?? null,
      wordCount: (orig || "").trim() === "" ? 0 : orig.trim().split(/\s+/).length,
    };
  };

  const persistTranslateAll = () => {
    saveTranslateToLS({
      ...captureTranslateState(),
      historyStack: historyStackRef.current,
      historyIndex: historyIndexRef.current,
      personalIdentifiersProtected: personalIdentifiersProtectedRef.current ?? personalIdentifiersProtected,
    });
  };

  const translateStatesEqual = (a, b) => {
    if (!a || !b) return false;
    return (a.originalText || "").trim() === (b.originalText || "").trim() &&
      (a.translatedText || "").trim() === (b.translatedText || "").trim();
  };

  const applyTranslateState = (state) => {
    if (!state) return;
    origDraftRef.current = null;
    origHtmlDraftRef.current = null;
    transDraftRef.current = null;
    transHtmlDraftRef.current = null;
    setOrigDraftActive(false);
    setTransDraftActive(false);

    originalTextRef.current = state.originalText || "";
    translatedTextRef.current = state.translatedText || "";
    savedOriginalRef.current = state.originalText || "";
    savedTranslatedRef.current = state.translatedText || "";
    lastSavedOriginalRef.current = state.originalText || "";
    lastSavedTranslatedRef.current = state.translatedText || "";
    setOriginalText(state.originalText || "");
    setTranslatedText(state.translatedText || "");
    setWordCount(state.wordCount || ((state.originalText || "").trim() === "" ? 0 : state.originalText.trim().split(/\s+/).length));
    if (state.selectedLanguage) {
      setSelectedLanguage(state.selectedLanguage);
      selectedLanguageRef.current = state.selectedLanguage;
    }
    const pi = state.personalIdentifiersProtected !== undefined ? state.personalIdentifiersProtected : null;
    personalIdentifiersProtectedRef.current = pi;
    setPersonalIdentifiersProtected(pi);
    currentTranslationIdRef.current = state.translationId || null;
    currentTranslationTypeRef.current = state.translationType || null;
    setCurrentTranslationType(state.translationType || null);
    setIsTextDirty(state.saved === false);
    setIsTranslatedDirty(state.saved === false);
    setEditEpoch((e) => e + 1);
    persistTranslateAll();
  };

  const syncTranslateUndoRedo = () => {
    const stack = historyStackRef.current || [];
    const idx = historyIndexRef.current;
    const committed = stack[idx];
    const hasDraft = (origHtmlDraftRef.current !== null && origHtmlDraftRef.current !== "") || (transHtmlDraftRef.current !== null && transHtmlDraftRef.current !== "");
    let hasUnsaved = false;
    if (hasDraft) {
      hasUnsaved = true;
    } else if (committed) {
      const current = captureTranslateState(true);
      hasUnsaved = !translateStatesEqual(current, committed);
    }
    const canUndo = idx > 0 || !!hasUnsaved;
    const canRedo = idx >= 0 && idx < stack.length - 1;
    setTranslateCanUndo((prev) => (prev !== canUndo ? canUndo : prev));
    setTranslateCanRedo((prev) => (prev !== canRedo ? canRedo : prev));
  };

  const resetTranslateHistoryToCurrent = () => {
    historyStackRef.current = [{ ...captureTranslateState(), saved: true }];
    historyIndexRef.current = 0;
    persistTranslateAll();
    syncTranslateUndoRedo();
  };

  const commitTranslateToHistory = (saved = true) => {
    const current = { ...captureTranslateState(), saved };
    const committed = historyStackRef.current[historyIndexRef.current];
    if (committed && translateStatesEqual(current, committed)) {
      if (committed) {
        committed.saved = saved;
        committed.personalIdentifiersProtected = current.personalIdentifiersProtected;
      }
      persistTranslateAll();
      syncTranslateUndoRedo();
      return;
    }
    historyStackRef.current = historyStackRef.current.slice(0, historyIndexRef.current + 1);
    historyStackRef.current.push(current);
    historyIndexRef.current = historyStackRef.current.length - 1;
    persistTranslateAll();
    syncTranslateUndoRedo();
  };

  const originalScroll = useCustomScrollbar(originalRef, `${activeTab}-${editEpoch}`);
  const translatedScroll = useCustomScrollbar(translatedRef, `${activeTab}-${editEpoch}`);
  const translateHistoryScroll = useCustomScrollbar(translateHistoryScrollRef);
  const focusOriginalScroll = useCustomScrollbar(focusOrigRef, focusPanel === "original" ? `focus-original-${editEpoch}` : null);
  const focusTranslatedScroll = useCustomScrollbar(focusTransRef, focusPanel === "translated" ? `focus-translated-${editEpoch}` : null);

  useEffect(() => {
    originalScroll.update();
    const t1 = setTimeout(() => originalScroll.update(), 100);
    const t2 = setTimeout(() => originalScroll.update(), 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [originalText, activeTab, isTranslating, isUploadingDoc]);

  useEffect(() => {
    translatedScroll.update();
    const t1 = setTimeout(() => translatedScroll.update(), 100);
    const t2 = setTimeout(() => translatedScroll.update(), 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [translatedText, streamingText, activeTab, isTranslating, isStreaming]);

  useEffect(() => {
    focusOriginalScroll.update();
    const t1 = setTimeout(() => focusOriginalScroll.update(), 100);
    const t2 = setTimeout(() => focusOriginalScroll.update(), 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalText, focusPanel]);

  useEffect(() => {
    focusTranslatedScroll.update();
    const t1 = setTimeout(() => focusTranslatedScroll.update(), 100);
    const t2 = setTimeout(() => focusTranslatedScroll.update(), 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translatedText, focusPanel]);

  // Track user scroll position in the translated panel to decide auto-scroll behavior
  useEffect(() => {
    const el = translatedRef.current;
    if (!el) return;

    const THRESHOLD = 80;

    const checkPosition = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      translatedNearBottomRef.current = scrollHeight - scrollTop - clientHeight <= THRESHOLD;
    };

    const handleUserInteraction = () => {
      translatedUserScrolledRef.current = true;
      checkPosition();
      clearTimeout(el._transScrollTimeout);
      el._transScrollTimeout = setTimeout(() => {
        checkPosition();
        translatedUserScrolledRef.current = false;
      }, 1500);
    };

    el.addEventListener("wheel", handleUserInteraction);
    el.addEventListener("touchstart", handleUserInteraction);
    el.addEventListener("pointerdown", handleUserInteraction);
    el.addEventListener("scroll", checkPosition, { passive: true });

    return () => {
      el.removeEventListener("wheel", handleUserInteraction);
      el.removeEventListener("touchstart", handleUserInteraction);
      el.removeEventListener("pointerdown", handleUserInteraction);
      el.removeEventListener("scroll", checkPosition);
      clearTimeout(el._transScrollTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editEpoch, activeTab]);

  useEffect(() => {
    translateHistoryScroll.update();
    const t1 = setTimeout(() => translateHistoryScroll.update(), 100);
    return () => clearTimeout(t1);
  }, [textHistory, docHistory]);

  useEffect(() => {
    if (activeTab !== "translate") return;
    const t1 = setTimeout(() => { originalScroll.update(); translatedScroll.update(); translateHistoryScroll.update(); }, 50);
    const t2 = setTimeout(() => { originalScroll.update(); translatedScroll.update(); translateHistoryScroll.update(); }, 200);
    const t3 = setTimeout(() => { originalScroll.update(); translatedScroll.update(); translateHistoryScroll.update(); }, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const filteredLanguages = countryData.filter((item) =>
    item.name.toLowerCase().includes(langSearch.toLowerCase())
  );

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsLangOpen(false);
      }
    };
    if (isLangOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isLangOpen]);

  const commitOriginalEdit = () => {
    origDraftRef.current = null;
    setOrigDraftActive(false);
    if (origHtmlDraftRef.current === null) return;
    const text = htmlToMarkdown(origHtmlDraftRef.current);
    origHtmlDraftRef.current = null;
    if (originalRef.current) origCommitScrollRef.current = originalRef.current.scrollTop;
    originalTextRef.current = text;
    setOriginalText(text);
    setEditEpoch(e => e + 1);
    setWordCount(text.trim() === "" ? 0 : text.trim().split(/\s+/).length);
    setIsTextDirty(text !== savedOriginalRef.current);
    syncTranslateUndoRedo();
  };

  const commitTranslatedEdit = () => {
    transDraftRef.current = null;
    if (transHtmlDraftRef.current === null) return;
    const text = htmlToMarkdown(transHtmlDraftRef.current);
    transHtmlDraftRef.current = null;
    setTransDraftActive(text.trim() !== "");
    if (translatedRef.current) transCommitScrollRef.current = translatedRef.current.scrollTop;
    translatedTextRef.current = text;
    setTranslatedText(text);
    setEditEpoch(e => e + 1);
    setWordCount(text.trim() === "" ? 0 : text.trim().split(/\s+/).length);
    setIsTranslatedDirty(text !== savedTranslatedRef.current);
    syncTranslateUndoRedo();
  };

  useLayoutEffect(() => {
    if (editEpoch === 0) return;
    if (originalRef.current) originalRef.current.scrollTop = origCommitScrollRef.current;
    if (translatedRef.current) translatedRef.current.scrollTop = transCommitScrollRef.current;
  }, [editEpoch]);

  const scheduleLiveSave = () => {
    if (liveSaveTimerRef.current) clearTimeout(liveSaveTimerRef.current);
    liveSaveTimerRef.current = setTimeout(() => {
      saveTranslateToLS({
        originalText: origHtmlDraftRef.current !== null ? htmlToMarkdown(origHtmlDraftRef.current) : originalTextRef.current,
        translatedText: transHtmlDraftRef.current !== null ? htmlToMarkdown(transHtmlDraftRef.current) : translatedTextRef.current,
        selectedLanguage: selectedLanguage,
        wordCount,
        translationId: currentTranslationIdRef.current,
        translationType: currentTranslationTypeRef.current,
        personalIdentifiersProtected: personalIdentifiersProtectedRef.current ?? personalIdentifiersProtected,
        historyStack: historyStackRef.current,
        historyIndex: historyIndexRef.current,
      });
    }, 500);
  };

  const getDomTextOffset = (el, node, offset) => {
    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.setEnd(node, offset);
      return range.toString().length;
    } catch (_e) {
      return null;
    }
  };

  const captureOrigSelection = () => {
    const el = originalRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return;
    const start = getDomTextOffset(el, range.startContainer, range.startOffset);
    const end = getDomTextOffset(el, range.endContainer, range.endOffset);
    if (start === null || end === null) return;
    origLastSelectionRef.current = { start, end };
  };

  const findTextPosition = (el, target) => {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let acc = 0;
    let n;
    while ((n = walker.nextNode())) {
      if (acc + n.data.length >= target) return { node: n, offset: target - acc };
      acc += n.data.length;
    }
    return null;
  };

  const restoreOrigSelection = () => {
    const el = originalRef.current;
    const saved = origLastSelectionRef.current;
    if (!el || !saved) return false;
    const total = (el.innerText || "").length;
    const startPos = findTextPosition(el, Math.min(saved.start, total));
    if (!startPos) return false;
    const endPos = findTextPosition(el, Math.min(saved.end, total)) || startPos;
    try {
      const range = document.createRange();
      range.setStart(startPos.node, startPos.offset);
      range.setEnd(endPos.node, endPos.offset);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      return true;
    } catch (_e) {
      return false;
    }
  };

  const captureTransSelection = () => {
    const el = translatedRef.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return;
    const start = getDomTextOffset(el, range.startContainer, range.startOffset);
    const end = getDomTextOffset(el, range.endContainer, range.endOffset);
    if (start === null || end === null) return;
    transLastSelectionRef.current = { start, end };
  };

  const restoreTransSelection = () => {
    const el = translatedRef.current;
    const saved = transLastSelectionRef.current;
    if (!el || !saved) return false;
    const total = (el.innerText || "").length;
    const startPos = findTextPosition(el, Math.min(saved.start, total));
    if (!startPos) return false;
    const endPos = findTextPosition(el, Math.min(saved.end, total)) || startPos;
    try {
      const range = document.createRange();
      range.setStart(startPos.node, startPos.offset);
      range.setEnd(endPos.node, endPos.offset);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      return true;
    } catch (_e) {
      return false;
    }
  };

  const placeCaretAtElEnd = (el) => {
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const scrollCaretIntoViewIfNeeded = (el) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0).cloneRange();
    let rect = range.getBoundingClientRect();
    if (!rect || (rect.top === 0 && rect.bottom === 0)) {
      if (range.startContainer.nodeType === Node.TEXT_NODE) {
        const probe = document.createRange();
        probe.setStart(range.startContainer, Math.max(0, range.startOffset - 1));
        probe.setEnd(range.startContainer, range.startOffset);
        rect = probe.getBoundingClientRect();
      }
    }
    if (!rect || (rect.height === 0 && rect.width === 0)) return;
    const box = el.getBoundingClientRect();
    if (rect.bottom > box.bottom) el.scrollTop += rect.bottom - box.bottom;
    else if (rect.top < box.top) el.scrollTop -= box.top - rect.top;
  };

  const afterPaint = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

  const handlePaste = async (target = "original") => {
    if (isTranslating || isStreaming || isUploadingDoc) return;
    const isTrans = target === "translated";
    const el = isTrans ? translatedRef.current : originalRef.current;
    if (!el) return;

    let text = "";
    if (navigator.clipboard && navigator.clipboard.read) {
      try {
        const items = await navigator.clipboard.read();
        for (const item of items) {
          if (item.types.includes("text/html")) {
            const blob = await item.getType("text/html");
            const html = await blob.text();
            if (/<table[\s>]/i.test(html)) {
              text = processTableContent(htmlToMarkdown(convertHtmlTablesToMarkdown(html)));
              break;
            }
          }
        }
      } catch (_e) { }
    }

    if (!text) {
      try {
        text = await navigator.clipboard.readText();
      } catch (clipboardError) {
        try {
          text = await fallbackPaste();
        } catch (fallbackError) {
          toastError(t.pasteFallbackError);
          el.focus({ preventScroll: true });
          return;
        }
      }
    }
    if (!text) return;

    text = processTableContent(text);

    const curVal = isTrans ? (translatedText || "") : (originalText || "");
    const curDraft = isTrans ? (transDraftRef.current || "") : (origDraftRef.current || "");
    if (!curVal.trim() && !curDraft.trim()) {
      if (isTrans) {
        translatedTextRef.current = text;
        transDraftRef.current = null;
        transHtmlDraftRef.current = null;
        setTransDraftActive(false);
        setTranslatedText(text);
        setEditEpoch((e) => e + 1);
        setIsTranslatedDirty(text !== savedTranslatedRef.current);
      } else {
        originalTextRef.current = text;
        origDraftRef.current = null;
        origHtmlDraftRef.current = null;
        setOrigDraftActive(false);
        setOriginalText(text);
        setEditEpoch((e) => e + 1);
        setWordCount(text.trim() === "" ? 0 : text.trim().split(/\s+/).length);
        setIsTextDirty(text !== savedOriginalRef.current);
        setShowLangMsg(text.trim() !== "" && !selectedLanguageRef.current);
      }
      syncTranslateUndoRedo();
      return;
    }

    el.focus({ preventScroll: true });

    const restoredSavedCaret = isTrans ? restoreTransSelection() : restoreOrigSelection();
    if (!restoredSavedCaret) {
      placeCaretAtElEnd(el);
    }

    const inserted = document.execCommand("insertText", false, text);
    if (inserted) {
      if (isTrans) {
        transDraftRef.current = el.innerText || "";
        transHtmlDraftRef.current = el.innerHTML;
        setTransDraftActive((el.innerText || "").trim() !== "");
        setIsTranslatedDirty(htmlToMarkdown(el.innerHTML) !== savedTranslatedRef.current);
      } else {
        origDraftRef.current = el.innerText || "";
        origHtmlDraftRef.current = el.innerHTML;
        setOrigDraftActive((el.innerText || "").trim() !== "");
        setIsTextDirty(htmlToMarkdown(el.innerHTML) !== savedOriginalRef.current);
        setShowLangMsg((el.innerText || "").trim() !== "" && !selectedLanguageRef.current);
      }
      setWordCount(((el.innerText) || "").trim() === "" ? 0 : (el.innerText || "").trim().split(/\s+/).length);
      afterPaint(() => scrollCaretIntoViewIfNeeded(el));
    } else {
      const cur = isTrans
        ? (typeof translatedTextRef.current === "string" ? translatedTextRef.current : "")
        : (typeof originalTextRef.current === "string" ? originalTextRef.current : "");
      const savedSel = isTrans ? transLastSelectionRef.current : origLastSelectionRef.current;
      const pos = savedSel ? Math.min(savedSel.start, cur.length) : cur.length;
      const newText = cur.slice(0, pos) + text + cur.slice(pos);
      if (isTrans) {
        translatedTextRef.current = newText;
        setTranslatedText(newText);
        setIsTranslatedDirty(newText !== savedTranslatedRef.current);
      } else {
        originalTextRef.current = newText;
        setOriginalText(newText);
        setIsTextDirty(newText !== savedOriginalRef.current);
        setShowLangMsg(newText.trim() !== "" && !selectedLanguageRef.current);
      }
      setWordCount(newText.trim() === "" ? 0 : newText.trim().split(/\s+/).length);
      syncTranslateUndoRedo();

      afterPaint(() => {
        if (restoredSavedCaret) scrollCaretIntoViewIfNeeded(el);
        else el.scrollTop = el.scrollHeight;
      });
    }
  };

  const fallbackPaste = () => {
    return new Promise((resolve, reject) => {
      const tempTextarea = document.createElement('textarea');
      tempTextarea.style.position = 'fixed';
      tempTextarea.style.top = '-9999px';
      tempTextarea.style.left = '-9999px';
      tempTextarea.style.width = '300px';
      tempTextarea.style.height = '150px';
      tempTextarea.style.opacity = '0';
      tempTextarea.style.pointerEvents = 'none';
      document.body.appendChild(tempTextarea);

      tempTextarea.focus();

      const success = document.execCommand('paste');
      if (success && tempTextarea.value) {
        const text = tempTextarea.value;
        document.body.removeChild(tempTextarea);
        resolve(text);
      } else {
        document.body.removeChild(tempTextarea);
        reject(new Error('Paste failed'));
      }
    });
  };

  const handleUndo = () => {
    if (activeTab === "transcribe") {
      if (transcribeUndoRef.current) transcribeUndoRef.current();
      return;
    }

    if (!translateCanUndo) return;

    const current = captureTranslateState();
    const stack = historyStackRef.current || [];
    const committed = stack[historyIndexRef.current];
    const hasUnsaved = committed && !translateStatesEqual(current, committed);

    if (hasUnsaved) {
      historyStackRef.current = stack.slice(0, historyIndexRef.current + 1);
      historyStackRef.current.push({ ...current, saved: false });
      applyTranslateState(historyStackRef.current[historyIndexRef.current]);
      syncTranslateUndoRedo();
      return;
    }

    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current -= 1;
    applyTranslateState(historyStackRef.current[historyIndexRef.current]);
    syncTranslateUndoRedo();
  };

  const handleRedo = () => {
    if (activeTab === "transcribe") {
      if (transcribeRedoRef.current) transcribeRedoRef.current();
      return;
    }
    if (!translateCanRedo) return;
    const stack = historyStackRef.current || [];
    if (historyIndexRef.current >= stack.length - 1) return;
    historyIndexRef.current += 1;
    applyTranslateState(historyStackRef.current[historyIndexRef.current]);
    syncTranslateUndoRedo();
  };

  const stripMarkdown = (text) => {
    if (!text) return text;
    let result = text;
    result = result.replace(/<\/?mark[^>]*>/gi, "");
    result = result.replace(/<[^>]+>/g, "");
    result = result.replace(/^#{1,6}\s+/gm, "");
    result = result.replace(/\*\*(.+?)\*\*/g, "$1");
    result = result.replace(/\*(.+?)\*/g, "$1");
    result = result.replace(/__(.+?)__/g, "$1");
    result = result.replace(/_(.+?)_/g, "$1");
    result = result.replace(/~~(.+?)~~/g, "$1");
    result = result.replace(/`{1,3}[^`]*`{1,3}/g, (m) => m.replace(/`/g, ""));
    result = result.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
    result = result.replace(/^>\s+/gm, "");
    result = result.replace(/^[-*+]\s+/gm, "");
    result = result.replace(/^\d+\.\s+/gm, "");
    result = result.replace(/^---+$/gm, "");
    result = result.replace(/^===+$/gm, "");
    result = result.replace(/^~~~+$/gm, "");
    return result;
  };

  const copyToClipboard = (text, onSuccess) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(onSuccess).catch(() => {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try { document.execCommand("copy"); onSuccess(); } catch { }
        document.body.removeChild(ta);
      });
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand("copy"); onSuccess(); } catch { }
      document.body.removeChild(ta);
    }
  };

  const handleCopyOriginal = () => {
    const latest = origHtmlDraftRef.current !== null ? htmlToMarkdown(origHtmlDraftRef.current) : originalText;
    if (!latest) return;
    copyToClipboard(stripMarkdown(latest), () => {
      setCopiedOriginal(true);
      setTimeout(() => setCopiedOriginal(false), 2000);
    });
  };

  const handleCopyTranslated = () => {
    const latest = transHtmlDraftRef.current !== null ? htmlToMarkdown(transHtmlDraftRef.current) : (streamingText || translatedText);
    if (!latest) return;
    copyToClipboard(stripMarkdown(latest), () => {
      setCopiedTranslated(true);
      setTimeout(() => setCopiedTranslated(false), 2000);
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = null;
    if (!file) return;
    if (isProcessingRef.current) return;

    // Show the file name in the upload area
    setUploadedFile(file);
    setShowLangMsg(!selectedLanguage);

    commitTranslateToHistory();
    setOriginalText("");
    setTranslatedText("");
    setTransDraftActive(false);
    setWordCount(0);

    try {
      TranslateService.setDocExtracting(true, file.name);
      isProcessingRef.current = true;
      setIsUploadingDoc(true);
      try {
        localStorage.setItem(
          "linko_pending_doc_extract",
          JSON.stringify({
            fileName: file.name,
            status: "extracting",
            timestamp: Date.now(),
          })
        );
      } catch (_e) { }
      const response = await TranslateService.extractDocumentText(file);

      // Handle the response - extract the text from all possible API response formats
      const rawData = response?.data;
      const dataObj = rawData?.data !== undefined ? rawData.data : rawData;
      let extracted = "";
      if (typeof dataObj === "string") {
        extracted = dataObj;
      } else if (dataObj && typeof dataObj === "object") {
        extracted =
          dataObj.extractedText ||
          dataObj.extracted_text ||
          dataObj.text ||
          dataObj.documentText ||
          dataObj.document_text ||
          dataObj.content ||
          dataObj.originalText ||
          dataObj.original_text ||
          dataObj.transcription ||
          dataObj.result ||
          "";
      }
      if (!extracted && rawData && typeof rawData === "object") {
        extracted =
          rawData.extractedText ||
          rawData.extracted_text ||
          rawData.text ||
          rawData.documentText ||
          rawData.document_text ||
          rawData.content ||
          rawData.originalText ||
          rawData.original_text ||
          rawData.transcription ||
          rawData.result ||
          "";
      }

      // Format the text with proper markdown headings for better UI display
      let formattedText = extracted;

      // If the text contains "Medical Record Summary", format it nicely
      if (extracted.includes("Medical Record Summary")) {
        // Keep the original formatting but ensure proper markdown rendering
        formattedText = extracted;
      }

      // Check if the text already contains markdown-style headers
      // If not, try to format common patterns
      const hasMarkdownHeaders = /^#/.test(formattedText) || /\n#/.test(formattedText);

      if (!hasMarkdownHeaders && formattedText) {
        // Try to format common patterns like "Patient", "Author", etc.
        const lines = formattedText.split('\n');
        const formattedLines = lines.map(line => {
          const trimmed = line.trim();
          // If line is a common section header, convert to heading
          const commonHeaders = ['Patient', 'Author', 'Table of Contents', 'MedicationRequest',
            'Appointment', 'Encounter', 'Condition', 'Immunization',
            'DiagnosticReport', 'Observation', 'Procedure', 'AllergyIntolerance',
            'CareTeam', 'CarePlan', 'FamilyMemberHistory', 'Coverage'];

          if (!trimmed.includes('|') && commonHeaders.some(header => trimmed.startsWith(header + ' '))) {
            return `### ${trimmed}`;
          }
          return line;
        });
        formattedText = formattedLines.join('\n');
      }

      // Preserve and format any tables from uploaded documents (PDF, DOCX, etc.)
      formattedText = processTableContent(formattedText);

      const words = formattedText.trim() === "" ? 0 : formattedText.trim().split(/\s+/).length;

      // Persist to localStorage immediately so switching tabs never loses it
      saveTranslateToLS({
        originalText: formattedText,
        translatedText: "",
        selectedLanguage: selectedLanguage || readLastLanguage() || "",
        wordCount: words,
        translationId: null,
        translationType: "document",
        historyStack: historyStackRef.current,
        historyIndex: historyIndexRef.current,
        personalIdentifiersProtected: null,
      });

      try {
        localStorage.setItem(
          "linko_pending_doc_extract",
          JSON.stringify({
            fileName: file.name,
            extractedText: formattedText,
            status: "completed",
            timestamp: Date.now(),
          })
        );
      } catch (_e) { }

      window.dispatchEvent(
        new CustomEvent("linko_doc_extract_completed", {
          detail: {
            text: formattedText,
            fileName: file.name,
          },
        })
      );

      currentTranslationIdRef.current = null;
      currentTranslationTypeRef.current = "document";
      setCurrentTranslationType("document");
      originalTextRef.current = formattedText;
      translatedTextRef.current = "";
      origDraftRef.current = null;
      origHtmlDraftRef.current = null;
      transDraftRef.current = null;
      transHtmlDraftRef.current = null;
      setOrigDraftActive(false);
      setOriginalText(formattedText);
      setWordCount(words);
      setTranslatedText("");
      setTransDraftActive(false);
      savedTranslatedRef.current = "";
      lastSavedTranslatedRef.current = "";
      setIsTranslatedDirty(false);
      savedOriginalRef.current = formattedText;
      lastSavedOriginalRef.current = formattedText;
      setIsTextDirty(false);
      setUploadedFile(null);
      setEditEpoch((e) => e + 1);

      resetTranslateHistoryToCurrent();

      requestAnimationFrame(() => {
        if (originalRef.current) originalRef.current.scrollTop = 0;
        if (translatedRef.current) translatedRef.current.scrollTop = 0;
      });

      toastSuccess(t.documentExtracted || "Document text extracted successfully");

    } catch (error) {
      TranslateService.setDocExtracting(false);
      window.dispatchEvent(
        new CustomEvent("linko_doc_extract_failed", {
          detail: {
            error: error?.response?.data?.message || t.documentTranslationFailed,
          },
        })
      );
      toastError(error?.response?.data?.message || t.documentTranslationFailed);
      // Keep the file name visible if extraction fails
    } finally {
      TranslateService.setDocExtracting(false);
      try {
        localStorage.removeItem("linko_pending_doc_extract");
      } catch (_e) { }
      isProcessingRef.current = false;
      setIsUploadingDoc(false);
    }
  };

  const scrollTranslatedIfNearBottom = () => {
    // Keep at top - do not auto-scroll to bottom
  };

  const handleTranslate = async () => {
    if (!selectedLanguage) {
      setLangError(true);
      setTimeout(() => setLangError(false), 2000);
      toastError(t.selectTargetLangFirst);
      return;
    }
    if (isProcessingRef.current) return;
    setShowLangMsg(false);

    let accumulated = "";
    try {
      isProcessingRef.current = true;
      setIsTranslating(true);
      if (privacyTimerRef.current) clearTimeout(privacyTimerRef.current);
      setPrivacyStatus("protecting");

      if (uploadedFile) {
        const response = await TranslateService.translateDocument(uploadedFile, selectedLanguage);
        const extracted = processTableContent(response?.data?.data?.extractedText || "");
        const translated = processTableContent(response?.data?.data?.translation || "");
        const id = response?.data?.data?._id || response?.data?.data?.id || null;
        const pi = response?.data?.data?.personalIdentifiersProtected ?? response?.data?.personalIdentifiersProtected ?? null;
        if (typeof pi === "number") setPersonalIdentifiersProtected(pi);
        currentTranslationIdRef.current = id;
        currentTranslationTypeRef.current = "document";
        setCurrentTranslationType("document");
        const words = extracted.trim() === "" ? 0 : extracted.trim().split(/\s+/).length;
        originalTextRef.current = extracted;
        translatedTextRef.current = translated;
        origDraftRef.current = null;
        origHtmlDraftRef.current = null;
        transDraftRef.current = null;
        transHtmlDraftRef.current = null;
        setOriginalText(extracted);
        setWordCount(words);
        setTranslatedText(translated);
        savedTranslatedRef.current = translated;
        lastSavedTranslatedRef.current = translated;
        setIsTranslatedDirty(false);
        savedOriginalRef.current = extracted;
        lastSavedOriginalRef.current = extracted;
        setIsTextDirty(false);
        setUploadedFile(null);
        commitTranslateToHistory(true);
        setPrivacyStatus("completed");
        if (privacyTimerRef.current) clearTimeout(privacyTimerRef.current);
        privacyTimerRef.current = setTimeout(() => {
          setPrivacyStatus("reminder");
        }, 5000);
        requestAnimationFrame(() => {
          if (originalRef.current) originalRef.current.scrollTop = 0;
          if (translatedRef.current) translatedRef.current.scrollTop = 0;
        });
      } else {
        let textToTranslate = (originalText || "").trim();
        if (origHtmlDraftRef.current !== null) {
          const md = htmlToMarkdown(origHtmlDraftRef.current).trim();
          if (md) textToTranslate = md;
        } else if (origDraftRef.current && origDraftRef.current.trim()) {
          textToTranslate = origDraftRef.current.trim();
        }
        origHtmlDraftRef.current = null;
        origDraftRef.current = null;
        transHtmlDraftRef.current = null;
        transDraftRef.current = null;
        setOrigDraftActive(false);
        setTransDraftActive(false);

        if (!textToTranslate) {
          isProcessingRef.current = false;
          setIsTranslating(false);
          return;
        }

        originalTextRef.current = textToTranslate;
        setOriginalText(textToTranslate);
        setWordCount(textToTranslate.trim() === "" ? 0 : textToTranslate.trim().split(/\s+/).length);

        // Ensure pre-translation state (original text on left, empty translation on right) is captured in history
        const stack = historyStackRef.current || [];
        const committed = stack[historyIndexRef.current];
        if (!committed || (committed.originalText || "").trim() !== textToTranslate.trim() || committed.translatedText) {
          commitTranslateToHistory(true);
        }

        setTranslateError("");
        dispatch(clearActiveTranslation());
        try {
          localStorage.removeItem("linko_active_translation");
          localStorage.setItem(
            "linko_pending_translation",
            JSON.stringify({
              originalText: textToTranslate,
              selectedLanguage,
              timestamp: Date.now(),
            })
          );
        } catch (_e) { }
        setIsTranslating(true);
        setIsStreaming(true);
        setTranslatedText("");
        savedTranslatedRef.current = "";
        streamingParagraphsRef.current = {};
        setStreamingText("");
        accumulated = "";
        let streamPi = null;
        const streamRes = await TranslateService.translateTextViaSocket(
          textToTranslate,
          selectedLanguage,
          (event) => {
            const evPi = event?.personalIdentifiersProtected ?? event?.personal_identifiers_protected;
            if (evPi !== null && evPi !== undefined && !isNaN(Number(evPi))) {
              streamPi = Number(evPi);
              setPersonalIdentifiersProtected(Number(evPi));
            }
            if (event.type === "paragraph") {
              streamingParagraphsRef.current[event.index] = event.translation;
              const fullText = Object.keys(streamingParagraphsRef.current)
                .sort((a, b) => Number(a) - Number(b))
                .map((k) => streamingParagraphsRef.current[k])
                .join("\n\n");
              accumulated = fullText;
              flushSync(() => {
                setStreamingText(fullText);
                setTranslatedText(fullText);
              });
              scrollTranslatedIfNearBottom();
            } else if (event.type === "chunk") {
              accumulated = event.accumulated;
              flushSync(() => {
                setStreamingText(accumulated);
                setTranslatedText(accumulated);
              });
              scrollTranslatedIfNearBottom();
            } else if (event.type === "done") {
              accumulated = event.translation || accumulated;
              const donePi = event?.personalIdentifiersProtected ?? event?.personal_identifiers_protected;
              if (donePi !== null && donePi !== undefined && !isNaN(Number(donePi))) {
                streamPi = Number(donePi);
                setPersonalIdentifiersProtected(Number(donePi));
              }
            }
          },
          dispatch
        );
        const resPi =
          streamRes?.personalIdentifiersProtected ??
          streamRes?.raw?.personalIdentifiersProtected ??
          streamRes?.raw?.data?.personalIdentifiersProtected ??
          streamRes?.personal_identifiers_protected ??
          streamPi;
        if (resPi !== null && resPi !== undefined && !isNaN(Number(resPi))) {
          setPersonalIdentifiersProtected(Number(resPi));
        }
        const finalTranslation = accumulated;
        currentTranslationIdRef.current = null;
        currentTranslationTypeRef.current = "text";
        setCurrentTranslationType("text");
        translatedTextRef.current = finalTranslation;
        originalTextRef.current = textToTranslate;
        setTranslatedText(finalTranslation);
        savedTranslatedRef.current = finalTranslation;
        lastSavedTranslatedRef.current = finalTranslation;
        setIsTranslatedDirty(false);
        savedOriginalRef.current = textToTranslate;
        lastSavedOriginalRef.current = textToTranslate;
        setIsTextDirty(false);
        setIsStreaming(false);
        setStreamingText("");
        streamingParagraphsRef.current = {};
        commitTranslateToHistory(true);
        refreshTextHistory();
        requestAnimationFrame(() => {
          if (translatedRef.current) translatedRef.current.scrollTop = 0;
          translatedScroll.update();
        });
        setPrivacyStatus("completed");
        if (privacyTimerRef.current) clearTimeout(privacyTimerRef.current);
        privacyTimerRef.current = setTimeout(() => {
          setPrivacyStatus("reminder");
        }, 5000);
      }
    } catch (error) {
      const partialTranslation =
        accumulated ||
        (Object.keys(streamingParagraphsRef.current || {}).length > 0
          ? Object.keys(streamingParagraphsRef.current)
            .sort((a, b) => Number(a) - Number(b))
            .map((k) => streamingParagraphsRef.current[k])
            .join("\n\n")
          : "") ||
        error?.partialResult?.translation ||
        "";

      if (partialTranslation) {
        currentTranslationIdRef.current = null;
        currentTranslationTypeRef.current = "text";
        setCurrentTranslationType("text");
        translatedTextRef.current = partialTranslation;
        originalTextRef.current = originalText;
        setTranslatedText(partialTranslation);
        savedTranslatedRef.current = partialTranslation;
        lastSavedTranslatedRef.current = partialTranslation;
        setIsTranslatedDirty(false);
        savedOriginalRef.current = originalText;
        lastSavedOriginalRef.current = originalText;
        setIsTextDirty(false);
        commitTranslateToHistory(true);
        refreshTextHistory();
        requestAnimationFrame(() => {
          if (translatedRef.current) translatedRef.current.scrollTop = 0;
          translatedScroll.update();
        });
      }

      setPrivacyStatus("default");
      const errMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        t.translationFailed;
      setTranslateError(errMsg);
      toastError(errMsg);
      setIsStreaming(false);
      setStreamingText("");
      streamingParagraphsRef.current = {};
    } finally {
      try {
        localStorage.removeItem("linko_pending_translation");
        localStorage.removeItem("linko_active_translation");
        dispatch(clearActiveTranslation());
      } catch (_e) { }
      isProcessingRef.current = false;
      setIsTranslating(false);
    }
  };

  const handleExport = () => {
    if (!translatedText) return;
    setShowExportPicker(true);
  };

  const doExport = (format) => {
    setShowExportPicker(false);

    const exportText = translatedText;

    if (!exportText) return;

    if (format === "text") {
      const plainText = stripMarkdown(exportText);
      const blob = new Blob([plainText], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Translation_${getTimestamp()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === "word") {
      const mdBlockWord = (label, md) => {
        const paragraphs = (md || "").split(/\n\s*\n/);
        const contentHtml = paragraphs
          .filter((p) => p.trim())
          .map((p) => `<p style="margin-bottom:8pt;">${markdownToHtml(p)}</p>`)
          .join("");
        return `<div style="margin-bottom:16pt;">
          ${label ? `<div style="font-weight:bold;font-size:13pt;color:#3D2B90;margin-bottom:6pt;border-bottom:1px solid #E2DDF4;padding-bottom:3pt;">${label}</div>` : ""}
          <div style="font-size:11pt;line-height:1.6;">${contentHtml}</div>
        </div>`;
      };
      const htmlBlocks = [];
      if (originalText) htmlBlocks.push(mdBlockWord("", originalText));
      htmlBlocks.push(mdBlockWord("", exportText));

      generateWordFromHtml(htmlBlocks, `Translation_${getTimestamp()}.docx`);
    } else {
      const mdBlock = (label, md) => {
        const paragraphs = (md || "").split(/\n\s*\n/);
        const contentHtml = paragraphs
          .filter((p) => p.trim())
          .map(
            (p) =>
              `<div data-pdf-keep-together="1">${markdownToHtml(p)}</div>`
          )
          .join("");
        return `<div style="margin-bottom:12px;">
          ${label ? `<div style="font-weight:700;font-size:12px;color:rgb(61,43,144);margin-bottom:4px;">${label}</div>` : ""}
          <div style="font-size:11px;line-height:1.5;">${MD_PDF_STYLE}${contentHtml}</div>
        </div>`;
      };
      const htmlBlocks = [];
      if (originalText) htmlBlocks.push(mdBlock("", originalText));
      htmlBlocks.push(mdBlock("", exportText));

      generatePdfFromHtml(htmlBlocks, `Translation_${getTimestamp()}.pdf`);
    }
  };

  const handleSave = () => {
    savedOriginalRef.current = originalTextRef.current;
    savedTranslatedRef.current = translatedTextRef.current;
    lastSavedOriginalRef.current = originalTextRef.current;
    lastSavedTranslatedRef.current = translatedTextRef.current;
    setIsTextDirty(false);
    setIsTranslatedDirty(false);
    commitTranslateToHistory(true);
    toastSuccess(t.saveSuccess || "Saved successfully");
  };

  const handleLangSelect = (item) => {
    setSelectedLanguage(item.name);
    selectedLanguageRef.current = item.name;
    saveLastLanguage(item.name);
    setShowLangMsg(false);
    setIsLangOpen(false);
    setLangSearch("");
  };

  const getLangLabel = (name) => {
    return name;
  };

  const isVertical = viewMode === "vertical";
  const isMac = isMacPlatform();
  const hasOrigContent = Boolean((originalText && originalText.trim()) || origDraftActive || (origDraftRef.current && origDraftRef.current.trim()));
  const hasTransContent = Boolean((translatedText && translatedText.trim()) || transDraftActive || (transDraftRef.current && transDraftRef.current.trim()));
  const hasTranslateContent = Boolean(hasOrigContent || hasTransContent || uploadedFile || textHistory.length > 0 || docHistory.length > 0);

  return (
    <div className={`flex flex-col flex-1 min-h-0 w-full items-center overflow-hidden translate-workspace ${isMac ? "platform-mac" : ""}`}>
      <style>{`
        .translate-workspace {
          --translate-font-size: 15px;
          --translate-line-height: 1.52;
          --translate-table-font-size: 13.5px;
          --translate-h1-size: 20px;
          --translate-h2-size: 18px;
          --translate-h3-size: 16px;
          --translate-h4-size: 14px;
          --translate-h5-size: 13px;
          --translate-h6-size: 12px;
        }
        .translate-workspace.platform-mac {
          --translate-font-size: 13.75px;
          --translate-line-height: 1.48;
          --translate-table-font-size: 12.5px;
          --translate-h1-size: 18px;
          --translate-h2-size: 16px;
          --translate-h3-size: 14.5px;
          --translate-h4-size: 13px;
          --translate-h5-size: 12px;
          --translate-h6-size: 11px;
        }
        .translate-workspace.platform-mac .translate-action-bar {
          margin-bottom: 0.5rem;
        }
        .translate-workspace.platform-mac .translate-lang-bar {
          min-height: 42px;
          padding-top: 0.35rem;
          padding-bottom: 0.35rem;
          margin-bottom: 0.5rem;
        }
        .translate-workspace.platform-mac .translate-card {
          padding: 0.8rem 0.95rem;
          border-radius: 20px;
        }
        .translate-workspace.platform-mac .translate-card-header {
          min-height: 34px;
          margin-bottom: 0.375rem;
        }
        .translate-workspace.platform-mac .translate-card-footer {
          margin-top: 0.375rem;
        }
        .translate-workspace.platform-mac .translate-bottom-bar {
          margin-top: 0.5rem;
        }
        @media (max-height: 900px) {
          .translate-workspace {
            --translate-font-size: 14px;
            --translate-line-height: 1.48;
            --translate-table-font-size: 12.5px;
          }
          .translate-workspace.platform-mac {
            --translate-font-size: 13.25px;
            --translate-line-height: 1.45;
            --translate-table-font-size: 12px;
          }
          .translate-workspace .translate-action-bar {
            margin-bottom: 0.5rem !important;
          }
          .translate-workspace .translate-lang-bar {
            min-height: 40px !important;
            padding-top: 0.35rem !important;
            padding-bottom: 0.35rem !important;
            margin-bottom: 0.5rem !important;
          }
          .translate-workspace .translate-card {
            padding: 0.75rem 0.875rem !important;
            border-radius: 18px !important;
          }
          .translate-workspace .translate-card-header {
            min-height: 32px !important;
            margin-bottom: 0.35rem !important;
          }
          .translate-workspace .translate-card-footer {
            margin-top: 0.35rem !important;
          }
          .translate-workspace .translate-bottom-bar {
            margin-top: 0.5rem !important;
          }
        }
      `}</style>

      <div className="flex flex-col flex-1 min-h-0 w-full px-2 sm:px-4 max-w-full">

        <div ref={actionBarRef} className="translate-action-bar action-bar-container flex flex-wrap items-center justify-center sm:justify-between gap-2 sm:gap-3 mb-4 w-full">
          <div className="flex items-center gap-2 sm:flex-1 sm:justify-start">
            {activeTab === "translate" && (
              <button
                onClick={() => {
                  uploadConfirmActionRef.current = "new";
                  setShowNewTranslateConfirm(true);
                }}
                disabled={!hasTranslateContent}
                className={`btn-gradient-border flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 font-bold text-dark-navy text-[10px] sm:text-xs uppercase tracking-wide whitespace-nowrap${!hasTranslateContent ? " opacity-40 cursor-not-allowed" : ""}`}
              >
                <img src={AddIcon} alt="new" className="h-3.5 w-3.5 sm:h-4 sm:w-4 object-contain flex-shrink-0" style={{ filter: "brightness(0) saturate(100%) invert(8%) sepia(47%) saturate(2500%) hue-rotate(240deg) brightness(85%)" }} />
                {t.newTranslate}
              </button>
            )}
            {activeTab === "transcribe" && (
              <button
                onClick={() => setShowNewTranscribeConfirm(true)}
                disabled={!transcribeHasContent && audioHistory.length === 0}
                className={`btn-gradient-border flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 font-bold text-dark-navy text-[10px] sm:text-xs uppercase tracking-wide whitespace-nowrap${(!transcribeHasContent && audioHistory.length === 0) ? " opacity-40 cursor-not-allowed" : ""}`}
              >
                <img src={AddIcon} alt="new" className="h-3.5 w-3.5 sm:h-4 sm:w-4 object-contain flex-shrink-0" style={{ filter: "brightness(0) saturate(100%) invert(8%) sepia(47%) saturate(2500%) hue-rotate(240deg) brightness(85%)" }} />
                {t.newTranscribe}
              </button>
            )}
            <button
              onClick={handleUndo}
              disabled={activeTab === "translate" ? !translateCanUndo : !transcribeCanUndo}
              className={`btn-gradient-border flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 font-bold text-dark-navy text-[10px] sm:text-xs uppercase tracking-wide whitespace-nowrap${(activeTab === "translate" ? !translateCanUndo : !transcribeCanUndo) ? " opacity-40 cursor-not-allowed" : ""}`}
            >
              <img src={UndoIcon} alt="undo" className="h-3.5 w-3.5 sm:h-4 sm:w-4 object-contain flex-shrink-0" style={{ filter: "brightness(0) saturate(100%) invert(8%) sepia(47%) saturate(2500%) hue-rotate(240deg) brightness(85%)" }} />
              {t.undo}
            </button>
            <button
              onClick={handleRedo}
              disabled={activeTab === "translate" ? !translateCanRedo : !transcribeCanRedo}
              className={`btn-gradient-border flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 font-bold text-dark-navy text-[10px] sm:text-xs uppercase tracking-wide whitespace-nowrap${(activeTab === "translate" ? !translateCanRedo : !transcribeCanRedo) ? " opacity-40 cursor-not-allowed" : ""}`}
            >
              <img src={UndoIcon} alt="redo" className="h-3.5 w-3.5 sm:h-4 sm:w-4 object-contain flex-shrink-0 scale-x-[-1]" style={{ filter: "brightness(0) saturate(100%) invert(8%) sepia(47%) saturate(2500%) hue-rotate(240deg) brightness(85%)" }} />
              {t.redo}
            </button>
          </div>

          <div className="order-3 xl:order-none basis-full xl:basis-auto w-full xl:w-auto flex items-center justify-center">
            <TranslateTabBar inline activeTab={activeTab} onTabChange={(tab) => {
              if (tab === "ai-chat") { navigate("/chat"); return; }
              setActiveTab(tab);
              navigate(tab === "transcribe" ? "/transcribe" : "/translate", { replace: true });
            }} />
          </div>

          <div className="flex items-center gap-2 sm:flex-1 sm:justify-end">
            {activeTab === "translate" && (
              <button
                onClick={() => setViewMode(v => v === "horizontal" ? "vertical" : "horizontal")}
                className="hidden sm:flex btn-gradient-border items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 font-bold text-dark-navy text-[10px] sm:text-xs uppercase tracking-wide whitespace-nowrap"
              >
                <img src={ViewIcon} alt="view" className="h-3.5 w-3.5 sm:h-4 sm:w-4 object-contain flex-shrink-0" style={{ filter: "brightness(0) saturate(100%) invert(8%) sepia(47%) saturate(2500%) hue-rotate(240deg) brightness(85%)" }} />
                {t.changeView}
              </button>
            )}
            {activeTab === "translate" && (
              <button
                onClick={() => { if (hasOrigContent || hasTransContent) setFocusPickerOpen(true); }}
                className={`btn-gradient-border flex flex-1 sm:flex-none items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-4 py-2 font-bold text-dark-navy text-[10px] sm:text-xs uppercase tracking-wide whitespace-nowrap${(!hasOrigContent && !hasTransContent) ? " opacity-50 cursor-not-allowed" : ""}`}
              >
                <img src={FocusIcon} alt="focus" className="h-3.5 w-3.5 sm:h-4 sm:w-4 object-contain flex-shrink-0" style={{ filter: "brightness(0) saturate(100%) invert(8%) sepia(47%) saturate(2500%) hue-rotate(240deg) brightness(85%)" }} />
                {t.focusMode}
              </button>
            )}
          </div>
        </div>

        {isLimitReached && (
          <div
            className={`w-full flex flex-wrap items-center justify-center gap-2 sm:gap-3.5 px-4 py-2 mb-3 rounded-[12px] shadow-xs flex-shrink-0 ${isTokenExhausted
              ? "bg-[#FDF2F2] border border-[#F8B4B4]"
              : "bg-[#FFF8EB] border border-[#F2C976]/70"
              }`}
          >
            <div className="flex items-center gap-2">
              <AlertCircle
                className={`h-4 w-4 flex-shrink-0 ${isTokenExhausted ? "text-[#E02424]" : "text-[#C07B00]"
                  }`}
              />
              <span
                className={`text-[12px] sm:text-[13px] font-roboto font-semibold leading-none ${isTokenExhausted ? "text-[#9B1C1C]" : "text-[#875800]"
                  }`}
              >
                {isTokenExhausted
                  ? (t.tokenLimitReachedMsg || "You have reached your token limit.")
                  : (t.tokenLimitWarning90Msg || "You have almost reached your token limit.")}
              </span>
            </div>
            <button
              type="button"
              onClick={() => navigate("/plans", { state: { tab: "plans" } })}
              className="flex items-center justify-center h-[28px] px-3.5 rounded-full bg-[#3D2B90] hover:bg-[#2c1e6b] text-white font-roboto font-bold text-[11px] uppercase tracking-wide leading-none transition-all shadow-xs cursor-pointer flex-shrink-0 active:scale-95"
            >
              {t.upgradePlanBtn || "Upgrade Plan"}
            </button>
          </div>
        )}

        <div className={`${activeTab === "transcribe" ? "flex flex-col flex-1 min-h-0" : "hidden"}`}>
          <TranscribeTab
            isActive={activeTab === "transcribe"}
            onReset={(fn) => { transcribeResetRef.current = fn; }}
            onUndo={(fn) => { transcribeUndoRef.current = fn; }}
            onUndoAvailable={setTranscribeCanUndo}
            onRedo={(fn) => { transcribeRedoRef.current = fn; }}
            onRedoAvailable={setTranscribeCanRedo}
            audioHistory={audioHistory}
            latestAudioTranslation={latestAudioTranslation}
            historyLoading={historyLoading}
            onSelectHistory={handleSelectHistoryItem}
            onTranscriptionComplete={refreshAudioHistory}
            onHasContent={(val) => setTranscribeHasContent(val)}
            availableTokens={availableTokens}
            hasAiAccess={hasAiAccess}
            onSendToTranslate={handleSendToTranslate}
          />
        </div>

        <div className={`${activeTab === "translate" ? "flex flex-col flex-1 min-h-0" : "hidden"}`}>
          {/* Language selection bar - single row */}
          <div className={`translate-lang-bar relative flex items-center justify-between px-4 sm:px-5 bg-white border border-[#E2DCF0] rounded-[20px] w-full min-h-[48px] py-2 shadow-sm ${isVertical ? "mb-2" : "mb-4"}`}>
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="font-museo font-bold text-[13px] sm:text-[14px] text-[#534E71] uppercase tracking-wide whitespace-nowrap select-none">
                {t.translateTo || "TRANSLATE TO:"}
              </span>

              {/* Target language selector dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsLangOpen((v) => !v)}
                  className={`flex items-center gap-2 h-[34px] px-3 sm:px-4 bg-[#F3F1F9] border border-[#E2DCF0] rounded-xl font-roboto font-bold text-[12px] sm:text-[13px] uppercase leading-none transition-colors max-w-[130px] sm:max-w-[180px] lg:max-w-[240px] ${langError ? "text-red-500 ring-2 ring-red-400" : "text-deep-purple hover:bg-[#EAE6F5]"}`}
                  title={selectedLanguage ? getLangLabel(selectedLanguage) : t.language}
                >
                  <span className="truncate min-w-0">{selectedLanguage ? getLangLabel(selectedLanguage) : t.language}</span>
                  <img src={DropdownIcon} alt="dropdown" className="h-3 w-3 flex-shrink-0 object-contain ml-auto" />
                </button>
                {isLangOpen && (
                  <div
                    className="absolute left-0 mt-2 w-52 bg-white rounded-xl shadow-lg z-50 border border-border"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-2">
                      <input
                        type="text"
                        placeholder={t.searchLanguage}
                        value={langSearch}
                        onChange={(e) => setLangSearch(e.target.value)}
                        className="w-full px-3 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                    <div className="max-h-44 overflow-y-auto dropdown-scroll">
                      {filteredLanguages.length > 0 ? (
                        filteredLanguages.map((item, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleLangSelect(item)}
                            className={`flex items-center justify-between w-full text-left px-4 py-2 text-xs border-b border-border last:border-0 ${item.name === selectedLanguage ? "text-[#3D2B90] font-bold bg-[#F3F1F9]" : "text-gray-700 hover:bg-gray"}`}
                          >
                            <span>{getLangLabel(item.name)}</span>
                            {item.name === selectedLanguage && (
                              <svg className="w-3.5 h-3.5 text-[#3D2B90] flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        ))
                      ) : (
                        <p className="px-4 py-2 text-xs text-gray-500 text-center">
                          {t.noLanguageFound}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleTranslate}
                disabled={isTranslating || !selectedLanguage || (!uploadedFile && !originalText.trim() && !origDraftActive && !(origDraftRef.current && origDraftRef.current.trim()))}
                className="flex items-center justify-center gap-2 h-[34px] px-4 sm:px-6 rounded-xl bg-[#3D2B90] hover:bg-[#2c1e6b] font-roboto font-bold text-[12px] sm:text-[13px] text-white uppercase leading-none whitespace-nowrap shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isTranslating ? (
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin flex-shrink-0" />
                    {t.translating}
                  </span>
                ) : (
                  <>
                    <span>{t.translateBtn}</span>
                    <span className="text-yellow-300 text-xs">✨</span>
                  </>
                )}
              </button>

              <div
                className="relative"
                onMouseEnter={() => !hasAiAccess && setShowAiTooltip(true)}
                onMouseLeave={() => setShowAiTooltip(false)}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (hasAiAccess) {
                      setAiMenuOpen(true);
                    } else if (isContactRequestSent()) {
                      setShowAlreadySentPopup(true);
                    } else {
                      setShowContactUs(true);
                    }
                  }}
                  className={`flex items-center justify-center w-[34px] h-[34px] rounded-xl bg-[#3D2B90] hover:bg-[#EAE6F5] border border-[#E2DCF0] transition-colors cursor-pointer`}
                  aria-label="Open AI menu"
                >
                  <img src={AIIcon} alt="AI" className="w-[18px] sm:w-[20px] h-[18px] sm:h-[20px] object-contain" />
                </button>
                {showAiTooltip && !hasAiAccess && (
                  <span className="absolute right-0 top-full mt-2 z-50 px-2.5 py-1.5 rounded bg-white text-gray-900 text-[10px] leading-snug pointer-events-none shadow-md border border-gray-200 max-w-[280px]">
                    {t.aiRulesOrgsOnly}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className={`flex flex-1 min-h-0 w-full ${isVertical ? "flex-col gap-2" : "flex-col gap-4 sm:flex-row"} ${!isVertical ? "sm:items-stretch" : ""}`}>

            {/* ORIGINAL TEXT Card */}
            <div className={`translate-card bg-white rounded-[24px] border border-[#E2DCF0] p-4 flex flex-col w-full flex-1 min-h-0 shadow-sm ${isVertical ? "" : "sm:min-w-0"}`}>
              <div className={`translate-card-header relative flex items-center justify-between w-full flex-shrink-0 ${isVertical ? "min-h-[34px] mb-2" : "min-h-[40px] mb-3"}`}>
                <div
                  className="relative flex-shrink-0"
                  style={{ display: "flex", alignItems: "center" }}
                  onMouseEnter={() => setShowUploadTooltip(true)}
                  onMouseLeave={() => setShowUploadTooltip(false)}
                >
                  <button
                    onClick={() => {
                      if (hasOrigContent || hasTransContent || uploadedFile) {
                        uploadConfirmActionRef.current = "upload";
                        setShowNewTranslateConfirm(true);
                      } else {
                        fileInputRef.current?.click();
                      }
                    }}
                    disabled={isTranslating || isUploadingDoc}
                    className="flex items-center gap-[6px] h-[32px] w-[32px] xl:w-auto px-0 xl:px-4 rounded-full bg-[#E8F1FF] border border-[#C5DCFF] text-[#3D2B90] hover:bg-[#d8e6ff] font-roboto font-bold text-xs uppercase leading-none justify-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    <img src={UploadFileIcon} alt="upload" className="h-3.5 w-3.5 object-contain flex-shrink-0" style={{ filter: "brightness(0) saturate(100%) invert(18%) sepia(60%) saturate(2200%) hue-rotate(240deg) brightness(85%)" }} />
                    <span className="hidden xl:inline">{t.uploadFile}</span>
                  </button>
                  {showUploadTooltip && (
                    <span
                      className="pointer-events-none absolute left-0 top-full mt-1.5 px-3 py-1.5 rounded-lg text-white text-[11px] whitespace-nowrap z-50 transition-opacity shadow-lg xl:hidden"
                      style={{ background: "#1A0E3E" }}
                    >
                      {t.uploadFileTooltip || "Upload File"}
                    </span>
                  )}
                </div>
                <input ref={fileInputRef} type="file" hidden accept=".txt,.doc,.docx,.pdf" onChange={handleFileUpload} />
                <span className="absolute left-1/2 -translate-x-1/2 font-roboto font-black text-xs sm:text-[14px] xl:text-[15px] text-[#42445A] uppercase leading-none tracking-wide text-center pointer-events-none whitespace-nowrap">
                  {t.originalText}
                </span>
                <div className="relative flex-shrink-0" style={{ display: "flex", alignItems: "center" }}>
                  <button
                    onClick={() => handlePaste("original")}
                    disabled={isTranslating || isUploadingDoc}
                    onMouseEnter={e => { const t = e.currentTarget.nextElementSibling; if (t) t.style.opacity = "1"; }}
                    onMouseLeave={e => { const t = e.currentTarget.nextElementSibling; if (t) t.style.opacity = "0"; }}
                    className="flex items-center gap-[6px] h-[32px] w-[32px] xl:w-auto px-0 xl:px-4 rounded-full bg-[#E8F1FF] border border-[#C5DCFF] text-[#3D2B90] hover:bg-[#d8e6ff] font-roboto font-bold text-xs uppercase leading-none justify-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    <img src={PasteIcon} alt="paste" className="h-3.5 w-3.5 object-contain flex-shrink-0" style={{ filter: "brightness(0) saturate(100%) invert(18%) sepia(60%) saturate(2200%) hue-rotate(240deg) brightness(85%)" }} />
                    <span className="hidden xl:inline">{t.paste}</span>
                  </button>
                  <span
                    className="pointer-events-none absolute right-0 top-full mt-1.5 px-2.5 py-1 rounded text-white text-[10px] whitespace-nowrap z-50 transition-opacity xl:hidden"
                    style={{ opacity: 0, background: "#1A0E3E" }}
                  >
                    {t.pasteTooltip || "Paste"}
                  </span>
                </div>
              </div>

              <TextPanel {...originalScroll}>
                <div className="relative flex flex-col flex-1 min-h-0 h-full">
                  {!originalText && !origDraftActive && !isTranslating && !isUploadingDoc && !uploadedFile && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none select-none">
                      <p className="text-[#3D2B90]/40 text-base sm:text-lg font-medium leading-snug text-center">
                        {t.originalPlaceholder}
                      </p>
                    </div>
                  )}
                  {!originalText && !isTranslating && !isUploadingDoc && uploadedFile && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none select-none gap-2">
                      <svg className="w-10 h-10 text-[#3D2B90]/30" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                      <p className="text-[#3D2B90]/50 text-sm font-medium text-center">{uploadedFile.name}</p>
                      <p className="text-[#3D2B90]/40 text-xs text-center">{t.selectLangAndTranslate}</p>
                    </div>
                  )}
                  {isUploadingDoc && (
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/70 backdrop-blur-[2px] select-none" aria-busy="true">
                      <div className="w-10 h-10 rounded-full border-[3px] border-[#E2DCF0] border-t-[#3D2B90] animate-spin" />
                      <span className="text-[#3D2B90]/80 text-[13px] font-roboto font-bold uppercase tracking-wide animate-pulse">
                        {t.uploadingDocument || "Uploading document..."}
                      </span>
                    </div>
                  )}
                  <div
                    key={`orig-editor-${editEpoch}`}
                    ref={originalRef}
                    style={{ fontSize: "var(--translate-font-size, 15px)" }}
                    className={`w-full h-full bg-transparent overflow-y-scroll no-scrollbar text-dark-navy ai-markdown caret-[#3D2B90] focus:outline-none ${isTranslating || isUploadingDoc ? "cursor-default" : "cursor-text"}`}
                    contentEditable={!isTranslating && !isUploadingDoc}
                    suppressContentEditableWarning
                    spellCheck={false}
                    onFocus={() => {
                      if (isTranslating || isUploadingDoc) return;
                      setOrigFocused(true);
                    }}
                    onKeyDown={(e) => {
                      if (isTranslating || isUploadingDoc) {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      if (isTranslating || isUploadingDoc) return;
                      const t = e.currentTarget.innerText || "";
                      origDraftRef.current = t;
                      origHtmlDraftRef.current = e.currentTarget.innerHTML;
                      const active = t.trim() !== "";
                      setOrigDraftActive((prev) => (prev !== active ? active : prev));
                      const newCount = t.trim() === "" ? 0 : t.trim().split(/\s+/).length;
                      setWordCount((prev) => (prev !== newCount ? newCount : prev));
                      const showLang = active && !selectedLanguageRef.current;
                      setShowLangMsg((prev) => (prev !== showLang ? showLang : prev));
                      scheduleLiveSave();
                      scrollCaretIntoViewIfNeeded(e.currentTarget);
                      syncTranslateUndoRedo();
                    }}
                    onBlur={() => {
                      if (isTranslating || isUploadingDoc) return;
                      captureOrigSelection();
                      setOrigFocused(false);
                      commitOriginalEdit();
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      if (isTranslating || isUploadingDoc) return;
                      const html = e.clipboardData.getData("text/html");
                      const plainText = e.clipboardData.getData("text/plain");

                      let textToInsert = "";
                      if (html && /<table[\s>]/i.test(html)) {
                        textToInsert = processTableContent(htmlToMarkdown(convertHtmlTablesToMarkdown(html)));
                      } else if (isTsvContent(plainText)) {
                        textToInsert = processTableContent(plainText);
                      } else {
                        textToInsert = processTableContent(plainText);
                      }
                      if (!textToInsert) textToInsert = plainText;

                      const curOrig = (originalText || "").trim();
                      const curDraft = (origDraftRef.current || "").trim();

                      if (!curOrig && !curDraft) {
                        originalTextRef.current = textToInsert;
                        origDraftRef.current = null;
                        origHtmlDraftRef.current = null;
                        setOrigDraftActive(false);
                        setOriginalText(textToInsert);
                        setEditEpoch((ep) => ep + 1);
                        const words = textToInsert.trim() === "" ? 0 : textToInsert.trim().split(/\s+/).length;
                        setWordCount(words);
                        setIsTextDirty(textToInsert !== savedOriginalRef.current);
                        setShowLangMsg(textToInsert.trim() !== "" && !selectedLanguageRef.current);
                        syncTranslateUndoRedo();
                      } else {
                        document.execCommand("insertText", false, textToInsert);
                        const currentText = e.currentTarget.innerText || "";
                        origDraftRef.current = currentText;
                        origHtmlDraftRef.current = e.currentTarget.innerHTML;
                        setOrigDraftActive(currentText.trim() !== "");
                        const words = currentText.trim() === "" ? 0 : currentText.trim().split(/\s+/).length;
                        setWordCount(words);
                        setShowLangMsg(currentText.trim() !== "" && !selectedLanguageRef.current);
                      }
                    }}
                  >
                    <FreezeWhileEditing focused={origFocused}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        key={`orig-${editEpoch}`}
                        components={MarkdownComponents}
                      >
                        {originalText}
                      </ReactMarkdown>
                    </FreezeWhileEditing>
                  </div>
                </div>
              </TextPanel>

              {/* Card Footer */}
              <div className={`translate-card-footer flex items-center justify-between flex-shrink-0 ${isVertical ? "mt-2" : "mt-3"}`}>
                <span className="text-[11px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {t.wordCount}: {wordCount}
                </span>
                {(originalText || origDraftActive) && (
                  <button
                    onClick={handleCopyOriginal}
                    title={copiedOriginal ? t.copied : t.copy}
                    className="flex items-center justify-center w-5 h-5 bg-transparent text-[#3D2B90] hover:opacity-75 transition-opacity flex-shrink-0"
                  >
                    {copiedOriginal ? (
                      <BadgeCheckIcon className="h-4 w-4 text-[#3D2B90]" />
                    ) : (
                      <img
                        src={CopyIcon}
                        alt="copy"
                        className="h-4 w-4 object-contain"
                        style={{ filter: "brightness(0) saturate(100%) invert(18%) sepia(60%) saturate(2200%) hue-rotate(240deg) brightness(85%)" }}
                      />
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* TRANSLATED TEXT Card */}
            <div className={`translate-card bg-white rounded-[24px] border border-[#E2DCF0] p-4 flex flex-col w-full flex-1 min-h-0 shadow-sm ${isVertical ? "" : "sm:min-w-0"}`}>
              <div className={`translate-card-header relative flex items-center justify-between w-full flex-shrink-0 ${isVertical ? "min-h-[34px] mb-2" : "min-h-[40px] mb-3"}`}>
                <div className="relative flex-shrink-0" style={{ display: "flex", alignItems: "center" }}>
                  {/* <button
                    onClick={() => handlePaste("translated")}
                    onMouseEnter={e => { const t = e.currentTarget.nextElementSibling; if (t) t.style.opacity = "1"; }}
                    onMouseLeave={e => { const t = e.currentTarget.nextElementSibling; if (t) t.style.opacity = "0"; }}
                    className="flex items-center gap-[6px] h-[32px] w-[32px] xl:w-auto px-0 xl:px-4 rounded-full bg-[#E8F1FF] border border-[#C5DCFF] text-[#3D2B90] hover:bg-[#d8e6ff] font-roboto font-bold text-xs uppercase leading-none justify-center whitespace-nowrap transition-all shadow-sm"
                  >
                    <img src={PasteIcon} alt="paste" className="h-3.5 w-3.5 object-contain flex-shrink-0" style={{ filter: "brightness(0) saturate(100%) invert(18%) sepia(60%) saturate(2200%) hue-rotate(240deg) brightness(85%)" }} />
                    <span className="hidden xl:inline">{t.paste}</span>
                  </button> */}
                  <span
                    className="pointer-events-none absolute left-0 top-full mt-1.5 px-2.5 py-1 rounded text-white text-[10px] whitespace-nowrap z-50 transition-opacity xl:hidden"
                    style={{ opacity: 0, background: "#1A0E3E" }}
                  >
                    {t.pasteTooltip || "Paste"}
                  </span>
                </div>
                <span className="absolute left-1/2 -translate-x-1/2 font-roboto font-black text-xs sm:text-[14px] xl:text-[15px] text-[#42445A] uppercase leading-none tracking-wide text-center pointer-events-none whitespace-nowrap">
                  {t.translatedText}
                </span>
                <div className="w-[32px] xl:w-[80px]" />
              </div>

              <TextPanel {...translatedScroll}>
                <div className="relative flex flex-col flex-1 min-h-0 h-full">
                  {/* {(isStreaming || isTranslating) && (
                    <div className="absolute top-2 right-4 z-20 flex items-center gap-1.5 px-3 py-1.5 bg-[#F3F1F9] border border-[#E2DCF0] rounded-full shadow-sm">
                      <span className="w-1.5 h-1.5 bg-[#3D2B90] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 bg-[#3D2B90] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 bg-[#3D2B90] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  )} */}
                  {(isStreaming || (isTranslating && !uploadedFile)) && !streamingText && !translatedText && (
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/70 backdrop-blur-[2px] select-none" aria-busy="true">
                      <div className="w-10 h-10 rounded-full border-[3px] border-[#E2DCF0] border-t-[#3D2B90] animate-spin" />
                      <span className="text-[#3D2B90]/80 text-[13px] font-roboto font-bold uppercase tracking-wide animate-pulse">
                        {t.translating || "Translating..."}
                      </span>
                    </div>
                  )}
                  {!streamingText && !isStreaming && !isTranslating && !translatedText && !transDraftActive && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none select-none">
                      <p className="text-[#3D2B90]/40 text-base sm:text-lg font-medium leading-snug text-center">
                        {t.translatedPlaceholder || "Translated text will appear here"}
                      </p>
                    </div>
                  )}
                  <div
                    key={`trans-editor-${editEpoch}`}
                    ref={translatedRef}
                    style={{ fontSize: "var(--translate-font-size, 15px)" }}
                    className={`w-full h-full bg-transparent overflow-y-scroll no-scrollbar text-dark-navy ai-markdown caret-[#3D2B90] focus:outline-none ${isTranslating || isStreaming ? "cursor-default" : "cursor-text"}`}
                    contentEditable={!isTranslating && !isStreaming}
                    suppressContentEditableWarning
                    spellCheck={false}
                    onFocus={() => {
                      if (isTranslating || isStreaming) return;
                      setTransFocused(true);
                    }}
                    onKeyDown={(e) => {
                      if (isTranslating || isStreaming) {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      if (isTranslating || isStreaming) return;
                      const t = e.currentTarget.innerText || "";
                      transDraftRef.current = t;
                      transHtmlDraftRef.current = e.currentTarget.innerHTML;
                      const active = t.trim() !== "";
                      setTransDraftActive((prev) => (prev !== active ? active : prev));
                      const newCount = t.trim() === "" ? 0 : t.trim().split(/\s+/).length;
                      setWordCount((prev) => (prev !== newCount ? newCount : prev));
                      scheduleLiveSave();
                      scrollCaretIntoViewIfNeeded(e.currentTarget);
                      syncTranslateUndoRedo();
                    }}
                    onBlur={() => {
                      if (isTranslating || isStreaming) return;
                      setTransFocused(false);
                      captureTransSelection();
                      commitTranslatedEdit();
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      if (isTranslating || isStreaming) return;
                      const html = e.clipboardData.getData("text/html");
                      const plainText = e.clipboardData.getData("text/plain");

                      let textToInsert = "";
                      if (html && /<table[\s>]/i.test(html)) {
                        textToInsert = processTableContent(htmlToMarkdown(convertHtmlTablesToMarkdown(html)));
                      } else if (isTsvContent(plainText)) {
                        textToInsert = processTableContent(plainText);
                      } else {
                        textToInsert = processTableContent(plainText);
                      }
                      if (!textToInsert) textToInsert = plainText;

                      const curTrans = (translatedText || "").trim();
                      const curDraft = (transDraftRef.current || "").trim();

                      if (!curTrans && !curDraft) {
                        translatedTextRef.current = textToInsert;
                        transDraftRef.current = null;
                        transHtmlDraftRef.current = null;
                        setTransDraftActive(false);
                        setTranslatedText(textToInsert);
                        setEditEpoch((ep) => ep + 1);
                        setIsTranslatedDirty(textToInsert !== savedTranslatedRef.current);
                        syncTranslateUndoRedo();
                      } else {
                        document.execCommand("insertText", false, textToInsert);
                        const currentText = e.currentTarget.innerText || "";
                        transDraftRef.current = currentText;
                        transHtmlDraftRef.current = e.currentTarget.innerHTML;
                        setTransDraftActive(currentText.trim() !== "");
                        setIsTranslatedDirty(htmlToMarkdown(e.currentTarget.innerHTML) !== savedTranslatedRef.current);
                      }
                    }}
                  >
                    <FreezeWhileEditing focused={transFocused}>
                      {streamingText ? (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          key={`trans-stream-${editEpoch}`}
                          components={MarkdownComponents}
                        >
                          {streamingText}
                        </ReactMarkdown>
                      ) : (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          key={`trans-${editEpoch}`}
                          components={MarkdownComponents}
                        >
                          {translatedText}
                        </ReactMarkdown>
                      )}
                    </FreezeWhileEditing>
                  </div>
                </div>
              </TextPanel>

              {/* Card Footer */}
              <div className={`translate-card-footer flex items-center justify-between flex-shrink-0 ${isVertical ? "mt-2" : "mt-3"}`}>
                {(translatedText || transDraftActive || streamingText) && (
                  <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap">
                    <span className="text-[11px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {t.wordCount}: {(function () {
                        const latest = transDraftRef.current !== null ? transDraftRef.current : (streamingText || translatedText || "");
                        return latest.trim() === "" ? 0 : latest.trim().split(/\s+/).length;
                      })()}
                    </span>
                    {personalIdentifiersProtected !== null && personalIdentifiersProtected !== undefined && !isNaN(Number(personalIdentifiersProtected)) && (
                      <span className="text-[10px] sm:text-[11px] font-bold text-[#3D2B90] uppercase tracking-wide flex items-center gap-1.5 bg-[#EDE9F8] px-2.5 py-0.5 rounded-full shadow-xs">
                        <Shield className="w-3.5 h-3.5 text-[#3D2B90] flex-shrink-0" />
                        <span>{t.personalIdentifiersProtected || "Protected Identifiers"}:</span>
                        <span className="font-black text-[#3D2B90]">{Number(personalIdentifiersProtected)}</span>
                      </span>
                    )}
                  </div>
                )}
                {(translatedText || transDraftActive || streamingText) && (
                  <button
                    onClick={handleCopyTranslated}
                    title={copiedTranslated ? t.copied : t.copy}
                    className="flex items-center justify-center w-5 h-5 bg-transparent text-[#3D2B90] hover:opacity-75 transition-opacity flex-shrink-0 ml-auto"
                  >
                    {copiedTranslated ? (
                      <BadgeCheckIcon className="h-4 w-4 text-[#3D2B90]" />
                    ) : (
                      <img
                        src={CopyIcon}
                        alt="copy"
                        className="h-4 w-4 object-contain"
                        style={{ filter: "brightness(0) saturate(100%) invert(18%) sepia(60%) saturate(2200%) hue-rotate(240deg) brightness(85%)" }}
                      />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Bar: Warning / Error Message Left, Export Button Right */}
          <div className={`translate-bottom-bar flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4 flex-shrink-0 ${isVertical ? "mt-2" : "mt-4"}`}>
            <div className={`flex items-center gap-2 flex-shrink-0 min-w-0 ${showLangMsg || availableTokens === 0 || translateError ? "bg-red-50 border border-red-100 px-2 py-1 rounded-md" : ""}`}>
              {showLangMsg || availableTokens === 0 || translateError ? (
                <AlertCircle className="h-[18px] w-[18px] text-red-500 flex-shrink-0" />
              ) : (
                <img src={WarningIcon} alt="warning" className="h-[18px] w-[18px] object-contain flex-shrink-0" />
              )}
              {showLangMsg ? (
                <span className="font-roboto font-bold text-[13px] text-red-500 leading-none tracking-normal truncate">
                  {t.selectTargetLangFirst || "Please select a language"}
                </span>
              ) : availableTokens === 0 ? (
                <span
                  className="font-roboto font-bold text-[13px] text-red-500 leading-none tracking-normal truncate cursor-pointer hover:underline"
                  onClick={() => navigate("/plans")}
                >
                  {t.upgradePlan || "Please upgrade your plan."}
                </span>
              ) : translateError ? (
                <span className="font-roboto font-bold text-[13px] text-red-500 leading-none tracking-normal truncate">
                  {translateError}
                </span>
              ) : privacyStatus === "protecting" ? (
                <span className="font-roboto font-bold text-[13px] text-[#3D2B90] leading-tight tracking-normal">
                  {t.protectingContent || "Protecting your content…"}
                  <br />
                  {t.checkingPersonalInfo || "Checking for personal information."}
                </span>
              ) : privacyStatus === "completed" ? (
                <span className="font-roboto font-bold text-[13px] text-[#3D2B90] leading-tight tracking-normal">
                  {(t.personalIdentifiersProtectedLabel || "Personal identifiers protected") + ": " + (personalIdentifiersProtected !== null && personalIdentifiersProtected !== undefined && !isNaN(Number(personalIdentifiersProtected)) ? Number(personalIdentifiersProtected) : 0)}
                </span>
              ) : privacyStatus === "reminder" ? (
                <span className="font-roboto font-bold text-[13px] text-[#3D2B90] leading-tight tracking-normal">
                  {t.translatePrivacyNotice || "Your information is not stored or used for AI training."}
                </span>
              ) : (
                <span className="font-roboto font-bold text-[13px] text-[#3D2B90] leading-tight tracking-normal">
                  {t.translatePrivacyNotice || "Your information is not stored or used for AI training."}
                </span>
              )}
            </div>
            <button
              onClick={handleExport}
              disabled={!translatedText}
              className={`btn-gradient-border w-full sm:w-auto flex items-center justify-center gap-2 h-[38px] px-6 font-roboto font-bold text-dark-navy text-xs uppercase tracking-wide whitespace-nowrap flex-shrink-0 transition-all ${translatedText
                ? "cursor-pointer hover:opacity-85"
                : "opacity-40 cursor-not-allowed"
                }`}
            >
              <img
                src={ExportIcon}
                alt="export"
                className="h-3.5 w-3.5 object-contain flex-shrink-0"
                style={{ filter: "brightness(0) saturate(100%) invert(8%) sepia(47%) saturate(2500%) hue-rotate(240deg) brightness(85%)" }}
              />
              <span>{t.exportBtn}</span>
            </button>
          </div>
        </div>

      </div>

      <MenuAIPanel
        isOpen={aiMenuOpen}
        onClose={() => setAiMenuOpen(false)}
        presets={presets}
        originalText={originalText}
        translatedText={translatedText}
        mode="translate"
        translationType={currentTranslationType || "text"}
        uploadedFile={uploadedFile}
        onResult={(improved) => {
          commitTranslateToHistory();
          if (improved && typeof improved === "object") {
            const orig = (improved.originalText !== undefined && improved.originalText !== null && improved.originalText !== "") ? improved.originalText : (originalTextRef.current || "");
            const trans = improved.translation || improved.translatedText || "";
            originalTextRef.current = orig;
            translatedTextRef.current = trans;
            setOriginalText(orig);
            savedOriginalRef.current = orig;
            lastSavedOriginalRef.current = orig;
            setIsTextDirty(false);
            setTranslatedText(trans);
            savedTranslatedRef.current = trans;
            lastSavedTranslatedRef.current = trans;
            setIsTranslatedDirty(false);
            const words = orig.trim() === "" ? 0 : orig.trim().split(/\s+/).length;
            setWordCount(words);
            commitTranslateToHistory(true);
            requestAnimationFrame(() => {
              if (translatedRef.current) translatedRef.current.scrollTop = 0;
              translatedScroll.update();
            });
          } else if (typeof improved === "string") {
            translatedTextRef.current = improved;
            setTranslatedText(improved);
            savedTranslatedRef.current = improved;
            lastSavedTranslatedRef.current = improved;
            setIsTranslatedDirty(false);
            commitTranslateToHistory(true);
            requestAnimationFrame(() => {
              if (translatedRef.current) translatedRef.current.scrollTop = 0;
              translatedScroll.update();
            });
          }
        }}
      />

      {showExportPicker && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 px-4"
          onClick={() => setShowExportPicker(false)}
          onKeyDown={(e) => { if (e.key === "Escape") setShowExportPicker(false); }}
          tabIndex={-1}
          ref={(el) => el?.focus()}
        >
          <div className="relative bg-white rounded-[27px] p-6 w-full max-w-sm flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowExportPicker(false)}
              className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center text-[#3D2B90] hover:opacity-70 transition-opacity"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L15 15M15 1L1 15" stroke="#3D2B90" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </button>
            <h2 className="font-roboto font-black text-[15px] text-[#42445A] uppercase text-center">{t.downloadAs}</h2>
            <div className="flex gap-2.5 sm:gap-3">
              <button
                type="button"
                onClick={() => doExport("text")}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-3 sm:px-4 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.text}
              </button>
              <button
                type="button"
                onClick={() => doExport("pdf")}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-3 sm:px-4 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.pdf}
              </button>
              <button
                type="button"
                onClick={() => doExport("word")}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-3 sm:px-4 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.word || "WORD"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewTranslateConfirm && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 px-4" onClick={() => { if (!isClearingTranslate) setShowNewTranslateConfirm(false); }}>
          <div className="bg-white rounded-[27px] p-6 w-full max-w-md flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-roboto font-black text-[15px] text-[#42445A] uppercase text-center">
              {t.newTranslateSessionTitle}
            </h2>
            <p className="text-[12px] text-[#9190A4] text-center font-roboto">
              {t.newTranslateConfirm}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                disabled={isClearingTranslate}
                onClick={() => setShowNewTranslateConfirm(false)}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-6 hover:bg-[#F3F1F9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.newSessionCancel}
              </button>
              <button
                type="button"
                disabled={isClearingTranslate}
                onClick={async () => {
                  setIsClearingTranslate(true);
                  try {
                    await HistoryService.deleteTranslations();
                    setShowNewTranslateConfirm(false);
                    try { localStorage.removeItem(TRANSLATE_LS_KEY); } catch (_e) { }
                    try { localStorage.removeItem("linko_personal_identifiers_protected"); } catch (_e) { }
                    setTextHistory([]);
                    setDocHistory([]);
                    setOriginalText("");
                    setTranslatedText("");
                    setOrigDraftActive(false);
                    setTransDraftActive(false);
                    origDraftRef.current = null;
                    origHtmlDraftRef.current = null;
                    transDraftRef.current = null;
                    transHtmlDraftRef.current = null;
                    originalTextRef.current = "";
                    translatedTextRef.current = "";
                    setUploadedFile(null);
                    setWordCount(0);
                    setEditEpoch((e) => e + 1);
                    setShowLangMsg(false);
                    setPersonalIdentifiersProtected(null);
                    if (privacyTimerRef.current) clearTimeout(privacyTimerRef.current);
                    setPrivacyStatus("default");
                    currentTranslationIdRef.current = null;
                    currentTranslationTypeRef.current = null;
                    setCurrentTranslationType(null);
                    savedOriginalRef.current = "";
                    savedTranslatedRef.current = "";
                    lastSavedOriginalRef.current = "";
                    lastSavedTranslatedRef.current = "";
                    const keepLang = readLastLanguage();
                    setSelectedLanguage(keepLang);
                    selectedLanguageRef.current = keepLang;
                    setIsTextDirty(false);
                    setIsTranslatedDirty(false);
                    historyStackRef.current = [{
                      originalText: "",
                      translatedText: "",
                      selectedLanguage: "",
                      translationId: null,
                      translationType: null,
                      wordCount: 0,
                      saved: true,
                    }];
                    historyIndexRef.current = 0;
                    setTranslateCanUndo(false);
                    setTranslateCanRedo(false);
                    if (
                      uploadConfirmActionRef.current === "upload" &&
                      keepLang
                    ) {
                      uploadConfirmActionRef.current = "new";
                      setTimeout(() => fileInputRef.current?.click(), 0);
                    }
                  } catch (err) {
                    setShowNewTranslateConfirm(false);
                    if (uploadConfirmActionRef.current === "upload") {
                      uploadConfirmActionRef.current = "new";
                    }
                    const errMsg = err?.response?.data?.message || err?.message || t.somethingWentWrong || "Failed to clear translations. Please check your connection.";
                    toastError(errMsg);
                  } finally {
                    setIsClearingTranslate(false);
                  }
                }}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none px-6 hover:bg-[#2e1f72] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isClearingTranslate ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin flex-shrink-0" />
                ) : (
                  t.newSessionConfirm
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewTranscribeConfirm && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 px-4" onClick={() => setShowNewTranscribeConfirm(false)}>
          <div className="bg-white rounded-[27px] p-6 w-full max-w-md flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-roboto font-black text-[15px] text-[#42445A] uppercase text-center">
              {t.newTranscribeSessionTitle}
            </h2>
            <p className="text-[12px] text-[#9190A4] text-center font-roboto">
              {t.newTranscribeConfirm}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowNewTranscribeConfirm(false)}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-6 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.newSessionCancel}
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowNewTranscribeConfirm(false);
                  try { await HistoryService.deleteAudioTranslations(); } catch (_e) { }
                  try { localStorage.removeItem("linko_transcribe_result"); } catch (_e) { }
                  setAudioHistory([]);
                  setLatestAudioTranslation(null);
                  if (transcribeResetRef.current) transcribeResetRef.current();
                }}
                className="flex-1 flex items-center justify-center h-[40px] rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none px-6 hover:bg-[#2e1f72] transition-colors"
              >
                {t.newSessionConfirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {focusPickerOpen && (
        <div className="fixed inset-0 z-[998] flex items-center justify-center bg-black/50 px-4" onClick={() => setFocusPickerOpen(false)}>
          <div className="bg-white rounded-[27px] p-6 w-full max-w-sm flex flex-col gap-4" onClick={e => e.stopPropagation()}>
            <h2 className="font-roboto font-black text-[15px] text-[#42445A] uppercase text-center tracking-wide">
              {t.focusMode}
            </h2>
            <p className="text-[12px] text-[#9190A4] text-center font-roboto">
              {t.focusPanelPrompt}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => { setFocusPanel("original"); setFocusPickerOpen(false); }}
                className="flex items-center justify-center h-[44px] rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none px-6 hover:bg-[#2e1f72] transition-colors"
              >
                {t.originalText}
              </button>
              <button
                onClick={() => { setFocusPanel("translated"); setFocusPickerOpen(false); }}
                className="flex items-center justify-center h-[44px] rounded-[67.52px] border-2 border-[#3D2B90] text-[#3D2B90] font-roboto font-semibold text-xs uppercase leading-none px-6 hover:bg-[#F3F1F9] transition-colors"
              >
                {t.translatedText}
              </button>
            </div>
          </div>
        </div>
      )}

      {focusPanel && (
        <div className="fixed inset-0 z-[999] flex flex-col bg-[#F3F1F9]">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 bg-white shadow-sm flex-shrink-0">
            <span className="font-roboto font-black text-[14px] sm:text-[16px] text-[#42445A] uppercase tracking-wide">
              {focusPanel === "original" ? t.originalText : t.translatedText}
            </span>
            <button
              onClick={() => setFocusPanel(null)}
              className="flex items-center gap-2 h-[36px] rounded-[67.52px] bg-[#3D2B90] text-white font-roboto font-semibold text-xs uppercase leading-none px-4 hover:bg-[#2e1f72] transition-colors"
            >
              ✕
            </button>
          </div>
          <div className="flex-1 min-h-0 p-4 sm:p-6">
            <div className="bg-white rounded-[27px] h-full p-4 flex flex-col">
              <div className="flex gap-[10.18px] flex-1 min-h-0 w-full">
                {focusPanel === "original" ? (
                  <div
                    ref={focusOrigRef}
                    className="flex-1 h-full bg-transparent overflow-y-scroll no-scrollbar text-sm text-dark-navy ai-markdown caret-[#3D2B90] focus:outline-none cursor-text"
                    contentEditable={!isTranslating && !isUploadingDoc}
                    suppressContentEditableWarning
                    spellCheck={false}
                    onFocus={() => {
                      if (isTranslating || isUploadingDoc) return;
                      setFocusOrigFocused(true);
                    }}
                    onKeyDown={(e) => {
                      if (isTranslating || isUploadingDoc) {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      if (isTranslating || isUploadingDoc) return;
                      const t = e.currentTarget.innerText || "";
                      origDraftRef.current = t;
                      origHtmlDraftRef.current = e.currentTarget.innerHTML;
                      const newCount = t.trim() === "" ? 0 : t.trim().split(/\s+/).length;
                      setWordCount((prev) => (prev !== newCount ? newCount : prev));
                      scheduleLiveSave();
                      scrollCaretIntoViewIfNeeded(e.currentTarget);
                    }}
                    onBlur={() => {
                      if (isTranslating || isUploadingDoc) return;
                      setFocusOrigFocused(false);
                      origDraftRef.current = null;
                      if (origHtmlDraftRef.current === null) return;
                      const text = htmlToMarkdown(origHtmlDraftRef.current);
                      origHtmlDraftRef.current = null;
                      originalTextRef.current = text;
                      setOriginalText(text);
                      setEditEpoch(e => e + 1);
                      setWordCount(text.trim() === "" ? 0 : text.trim().split(/\s+/).length);
                      setIsTextDirty(text !== savedOriginalRef.current);
                      syncTranslateUndoRedo();
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      if (isTranslating || isUploadingDoc) return;
                      const text = e.clipboardData.getData("text/plain");
                      document.execCommand("insertText", false, text);
                    }}
                  >
                    <FreezeWhileEditing focused={focusOrigFocused}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        key={`origFocus-${editEpoch}`}
                        components={MarkdownComponents}
                      >
                        {originalText}
                      </ReactMarkdown>
                    </FreezeWhileEditing>
                  </div>
                ) : (
                  <div
                    ref={focusTransRef}
                    className="flex-1 h-full bg-transparent overflow-y-scroll no-scrollbar text-sm text-dark-navy ai-markdown caret-[#3D2B90] focus:outline-none cursor-text"
                    contentEditable={!isTranslating && !isStreaming}
                    suppressContentEditableWarning
                    spellCheck={false}
                    onFocus={() => {
                      if (isTranslating || isStreaming) return;
                      setFocusTransFocused(true);
                    }}
                    onKeyDown={(e) => {
                      if (isTranslating || isStreaming) {
                        e.preventDefault();
                      }
                    }}
                    onInput={(e) => {
                      if (isTranslating || isStreaming) return;
                      const t = e.currentTarget.innerText || "";
                      transDraftRef.current = t;
                      transHtmlDraftRef.current = e.currentTarget.innerHTML;
                      const newCount = t.trim() === "" ? 0 : t.trim().split(/\s+/).length;
                      setWordCount((prev) => (prev !== newCount ? newCount : prev));
                      scheduleLiveSave();
                      scrollCaretIntoViewIfNeeded(e.currentTarget);
                    }}
                    onBlur={() => {
                      if (isTranslating || isStreaming) return;
                      setFocusTransFocused(false);
                      transDraftRef.current = null;
                      if (transHtmlDraftRef.current === null) return;
                      const text = htmlToMarkdown(transHtmlDraftRef.current);
                      transHtmlDraftRef.current = null;
                      translatedTextRef.current = text;
                      setTranslatedText(text);
                      setEditEpoch(e => e + 1);
                      setWordCount(text.trim() === "" ? 0 : text.trim().split(/\s+/).length);
                      setIsTranslatedDirty(text !== savedTranslatedRef.current);
                      syncTranslateUndoRedo();
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      if (isTranslating || isStreaming) return;
                      const text = e.clipboardData.getData("text/plain");
                      document.execCommand("insertText", false, text);
                    }}
                  >
                    <FreezeWhileEditing focused={focusTransFocused}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        key={`focusTrans-${editEpoch}`}
                        components={MarkdownComponents}
                      >
                        {translatedText}
                      </ReactMarkdown>
                    </FreezeWhileEditing>
                  </div>
                )}
                {focusPanel === "original" ? (
                  <div
                    ref={focusOriginalScroll.trackRef}
                    className="relative w-[14px] lg:w-[17.94px] h-full bg-[#C7BFE4] rounded-[17px] flex-shrink-0 cursor-pointer"
                    style={{ visibility: focusOriginalScroll.hasOverflow ? "visible" : "hidden" }}
                    onPointerDown={focusOriginalScroll.onTrackPointerDown}
                  >
                    {focusOriginalScroll.hasOverflow && (
                      <div
                        ref={focusOriginalScroll.thumbRef}
                        className="absolute w-full bg-[#3D2B90] rounded-[10px] cursor-grab active:cursor-grabbing"
                        style={{ top: `${focusOriginalScroll.thumbTop}px`, height: `${focusOriginalScroll.thumbH}px` }}
                        onPointerDown={focusOriginalScroll.onThumbPointerDown}
                      />
                    )}
                  </div>
                ) : (
                  <div
                    ref={focusTranslatedScroll.trackRef}
                    className="relative w-[14px] lg:w-[17.94px] h-full bg-[#C7BFE4] rounded-[17px] flex-shrink-0 cursor-pointer"
                    style={{ visibility: focusTranslatedScroll.hasOverflow ? "visible" : "hidden" }}
                    onPointerDown={focusTranslatedScroll.onTrackPointerDown}
                  >
                    {focusTranslatedScroll.hasOverflow && (
                      <div
                        ref={focusTranslatedScroll.thumbRef}
                        className="absolute w-full bg-[#3D2B90] rounded-[10px] cursor-grab active:cursor-grabbing"
                        style={{ top: `${focusTranslatedScroll.thumbTop}px`, height: `${focusTranslatedScroll.thumbH}px` }}
                        onPointerDown={focusTranslatedScroll.onThumbPointerDown}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showAlreadySentPopup && (
        <AlreadySentPopup
          t={t}
          onClose={() => setShowAlreadySentPopup(false)}
        />
      )}
      {showContactUs && (
        <ContactUsPopup
          t={t}
          onClose={() => setShowContactUs(false)}
          onSubmit={handleContactUsSubmit}
          isSubmitting={isContactUsSubmitting}
          isSuccess={false}
          prefilledEmail={readStoredEmail() || ""}
        />
      )}
      {contactUsSuccess && (
        <ContactUsPopup
          t={t}
          onClose={() => setContactUsSuccess(false)}
          onSubmit={() => { }}
          isSubmitting={false}
          isSuccess={true}
        />
      )}
    </div>
  );
};

export default Translate;